import { useState, useEffect, useRef } from "react";
import axiosInstance from "../axiosinstance";
import { format, addDays, parse } from "date-fns";
import "./InvoiceForm.css";
import "./Forms.css";
import { createPortal } from "react-dom";
import { MdCancel } from "react-icons/md";
import {
  Snackbar,
  Alert as MuiAlert,
  Modal,
  Box,
} from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
// import { Close as CloseIcon } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select"; // Import react-select
import DatePicker from "react-datepicker";
import ClientModal from "../Popup/ClientModal";
import { X } from "lucide-react";
import ServiceMasterModal from "../Popup/ServiceMasterModal";
import { LuPlus } from "react-icons/lu";
import { ClipLoader } from "react-spinners"; // Spinner library
import "react-datepicker/dist/react-datepicker.css";

const formatDate = (date) => {
  const d = new Date(date);
  return format(d, "dd-MM-yyyy"); // DD-MM-YYYY
};

const FULL_DATE_REGEX = /^(\d{1,2})-(\d{1,2})-(\d{2}|\d{4})$/;

const normalizeInvoiceDateInput = (value, { allowPartial = true } = {}) => {
  if (!value) return { value: "", error: "" };

  // Replace / . with -
  let cleaned = value.trim().replace(/[./]/g, "-").replace(/-+/g, "-");

  // ❌ Catch if string starts or ends with hyphen
  if (cleaned.startsWith("-") || cleaned.endsWith("-")) {
    return {
      value: cleaned,
      error: "Invalid date format",
    };
  }

  // If it contains anything other than digits and '-', show error
  if (/[^0-9-]/.test(cleaned)) {
    return { value: cleaned, error: "Only numbers allowed" };
  }

  // ❌ NEW: Check for very long year (more than 4 digits)
  const LONG_YEAR_PATTERN = /^(\d{1,2})-(\d{5,})$/;
  if (LONG_YEAR_PATTERN.test(cleaned)) {
    return {
      value: cleaned,
      error: "Invalid year format",
    };
  }

  const INVALID_MONTH_YEAR_PATTERN = /^(\d{1,2})-(\d{3})-(\d{3})$/;
  if (INVALID_MONTH_YEAR_PATTERN.test(cleaned)) {
    return {
      value: cleaned,
      error: "Invalid date format. Use DD-MM-YYYY",
    };
  }

  const THREE_DIGIT_DAY_PATTERN = /^(\d{3})-(\d{1,2})-(\d{2,4})$/;
  if (THREE_DIGIT_DAY_PATTERN.test(cleaned)) {
    return {
      value: cleaned,
      error: "Invalid day format. Use DD-MM-YYYY",
    };
  }

  // ❌ Explicitly catch 3-digit years like "1-4-200"
  const THREE_DIGIT_YEAR = /^(\d{1,2})-(\d{1,2})-(\d{3})$/;
  if (THREE_DIGIT_YEAR.test(cleaned)) {
    return {
      value: cleaned,
      error: "Enter full year (YYYY)",
    };
  }

  // ❌ Catch year in middle position like "12-1999-23"
  const YEAR_IN_MIDDLE = /^(\d{1,2})-(\d{4})-(\d{1,2})$/;
  if (YEAR_IN_MIDDLE.test(cleaned)) {
    return {
      value: cleaned,
      error: "Invalid format. Use DD-MM-YYYY",
    };
  }

  // ❌ Catch year at start like "1999-45-45"
  const YEAR_AT_START = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
  if (YEAR_AT_START.test(cleaned)) {
    return {
      value: cleaned,
      error: "Invalid format. Use DD-MM-YYYY",
    };
  }

  // ❌ Catch 2-digit year that looks invalid like "13-12-13"
  const possibleInvalidYear = /^(\d{2})-(\d{1,2})-(\d{2})$/;
  if (possibleInvalidYear.test(cleaned)) {
    const match = cleaned.match(possibleInvalidYear);
    const firstPart = parseInt(match[1], 10);
    const thirdPart = parseInt(match[3], 10);

    if (firstPart > 12 && thirdPart <= 31) {
      return {
        value: cleaned,
        error: "Enter full year (YYYY)",
      };
    }
  }

  // ❌ Catch single digit year like "1-1-1"
  const SINGLE_DIGIT_YEAR = /^(\d{1,2})-(\d{1,2})-(\d{1})$/;
  if (SINGLE_DIGIT_YEAR.test(cleaned)) {
    return {
      value: cleaned,
      error: "Enter full year (YYYY)",
    };
  }

  // While the user is still typing (e.g. "1", "11-", "11-10-2")
  // don't force validation yet
  if (allowPartial && !FULL_DATE_REGEX.test(cleaned)) {
    return { value: cleaned, error: "" };
  }

  // Now enforce strict 2-2-4 format
  if (!/^(\d{2})-(\d{2})-(\d{4})$/.test(cleaned)) {
    return {
      value: cleaned,
      error: "Invalid format. Use DD-MM-YYYY",
    };
  }

  // Now we expect DD-M-YY or DD-M-YYYY
  const match = cleaned.match(FULL_DATE_REGEX);
  if (!match) {
    return { value: cleaned, error: "Invalid date format" };
  }

  let day = parseInt(match[1], 10);
  let month = parseInt(match[2], 10);
  const yearRaw = match[3];

  // Convert year to a proper 4-digit number for validation / due date
  let year = parseInt(yearRaw, 10);

  // 2-digit year → 2000–2099 (e.g. 25 → 2025)
  if (yearRaw.length === 2) {
    year = 2000 + year;
  }

  // Range checks
  if (month < 1 || month > 12) {
    return { value: cleaned, error: "Month must be between 1–12" };
  }
  if (day < 1 || day > 31) {
    return { value: cleaned, error: "Day must be between 1–31" };
  }

  if (day === 0 || month === 0) {
    return {
      value: cleaned,
      error: "Day and month cannot be zero",
    };
  }

  if ((day < 1 || day > 31) && (month < 1 || month > 12)) {
    return {
      value: cleaned,
      error: "Invalid day and month",
    };
  }

  // Calendar validity (Feb, 30/31, leap year, etc.)
  const jsDate = new Date(year, month - 1, day);
  if (
    jsDate.getFullYear() !== year ||
    jsDate.getMonth() + 1 !== month ||
    jsDate.getDate() !== day
  ) {
    return { value: cleaned, error: "Invalid calendar date" };
  }

  // Build normalized DD-MM-YYYY for non-partial usage (due date calc)
  const normalized = `${String(day).padStart(2, "0")}-${String(month).padStart(
    2,
    "0"
  )}-${year}`;

  // 👉 For typing in the field: DON'T rewrite what user typed
  if (allowPartial) {
    return { value: cleaned, error: "" };
  }

  // 👉 For due-date calculation: return normalized full year (e.g. 2025)
  return { value: normalized, error: "" };
};
 
const normalizeBirthDateInput = (value, { allowPartial = true } = {}) => {
  if (!value) return { value: "", error: "" };

  // Replace / . with -
  let cleaned = value.trim().replace(/[./]/g, "-").replace(/-+/g, "-");

  // ❌ Catch if string starts or ends with hyphen
  if (cleaned.startsWith("-") || cleaned.endsWith("-")) {
    return {
      value: cleaned,
      error: "Invalid date format",
    };
  }

  // If it contains anything other than digits and '-', show error
  if (/[^0-9-]/.test(cleaned)) {
    return { value: cleaned, error: "Only numbers allowed" };
  }

  // ❌ Check for very long year (more than 4 digits)
  const LONG_YEAR_PATTERN = /^(\d{1,2})-(\d{5,})$/;
  if (LONG_YEAR_PATTERN.test(cleaned)) {
    return {
      value: cleaned,
      error: "Invalid year format",
    };
  }

  const INVALID_MONTH_YEAR_PATTERN = /^(\d{1,2})-(\d{3})-(\d{3})$/;
  if (INVALID_MONTH_YEAR_PATTERN.test(cleaned)) {
    return {
      value: cleaned,
      error: "Invalid date format. Use DD-MM-YYYY",
    };
  }

  const THREE_DIGIT_DAY_PATTERN = /^(\d{3})-(\d{1,2})-(\d{2,4})$/;
  if (THREE_DIGIT_DAY_PATTERN.test(cleaned)) {
    return {
      value: cleaned,
      error: "Invalid day format. Use DD-MM-YYYY",
    };
  }

  // ❌ Explicitly catch 3-digit years like "1-4-200"
  const THREE_DIGIT_YEAR = /^(\d{1,2})-(\d{1,2})-(\d{3})$/;
  if (THREE_DIGIT_YEAR.test(cleaned)) {
    return {
      value: cleaned,
      error: "Enter full year (YYYY)",
    };
  }

  // ❌ Catch year in middle position like "12-1999-23"
  const YEAR_IN_MIDDLE = /^(\d{1,2})-(\d{4})-(\d{1,2})$/;
  if (YEAR_IN_MIDDLE.test(cleaned)) {
    return {
      value: cleaned,
      error: "Invalid format. Use DD-MM-YYYY",
    };
  }

  // ❌ Catch year at start like "1999-45-45"
  const YEAR_AT_START = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
  if (YEAR_AT_START.test(cleaned)) {
    return {
      value: cleaned,
      error: "Invalid format. Use DD-MM-YYYY",
    };
  }

  // ❌ Catch 2-digit year that looks invalid like "13-12-13"
  const possibleInvalidYear = /^(\d{2})-(\d{1,2})-(\d{2})$/;
  if (possibleInvalidYear.test(cleaned)) {
    const match = cleaned.match(possibleInvalidYear);
    const firstPart = parseInt(match[1], 10);
    const thirdPart = parseInt(match[3], 10);

    if (firstPart > 12 && thirdPart <= 31) {
      return {
        value: cleaned,
        error: "Enter full year (YYYY)",
      };
    }
  }

  // ❌ Catch single digit year like "1-1-1"
  const SINGLE_DIGIT_YEAR = /^(\d{1,2})-(\d{1,2})-(\d{1})$/;
  if (SINGLE_DIGIT_YEAR.test(cleaned)) {
    return {
      value: cleaned,
      error: "Enter full year (YYYY)",
    };
  }

  // While the user is still typing (e.g. "1", "11-", "11-10-2")
  // don't force validation yet
  if (allowPartial && !FULL_DATE_REGEX.test(cleaned)) {
    return { value: cleaned, error: "" };
  }

  // Now we expect DD-M-YY or DD-M-YYYY
  const match = cleaned.match(FULL_DATE_REGEX);
  if (!match) {
    return { value: cleaned, error: "Invalid date format" };
  }

  let day = parseInt(match[1], 10);
  let month = parseInt(match[2], 10);
  const yearRaw = match[3];

  // Convert year to a proper 4-digit number for validation
  let year = parseInt(yearRaw, 10);

  // 2-digit year → 2000–2099 (e.g. 25 → 2025)
  if (yearRaw.length === 2) {
    year = 2000 + year;
  }

  // Range checks
  if (month < 1 || month > 12) {
    return { value: cleaned, error: "Month must be between 1–12" };
  }
  if (day < 1 || day > 31) {
    return { value: cleaned, error: "Day must be between 1–31" };
  }

  if (day === 0 || month === 0) {
    return {
      value: cleaned,
      error: "Day and month cannot be zero",
    };
  }

  if ((day < 1 || day > 31) && (month < 1 || month > 12)) {
    return {
      value: cleaned,
      error: "Invalid day and month",
    };
  }

  // Calendar validity (Feb, 30/31, leap year, etc.)
  const jsDate = new Date(year, month - 1, day);
  if (
    jsDate.getFullYear() !== year ||
    jsDate.getMonth() + 1 !== month ||
    jsDate.getDate() !== day
  ) {
    return { value: cleaned, error: "Invalid calendar date" };
  }

  // ❌ Check for unrealistic historical dates (before 1900)
  if (year < 1935) {
    return {
      value: cleaned,
      error: "Invalid year. Please enter a valid year (1935-2099)",
    };
  }

  // ❌ Check if date is in the future
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time for accurate comparison

  const birthDate = new Date(year, month - 1, day);
  birthDate.setHours(0, 0, 0, 0);

  // if (birthDate > today) {
  //   return {
  //     value: cleaned,
  //     error: "Birth date cannot be in the future",
  //   };
  // }

  // ✅ Check if user is at least 18 years old
  let age = today.getFullYear() - year;
  const monthDiff = today.getMonth() - (month - 1);
  const dayDiff = today.getDate() - day;

  // Adjust age if birthday hasn't occurred yet this year
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }

  // if (!allowPartial && age < 18) {
  //   return {
  //     value: cleaned,
  //     error: "Client must be at least 18 years old",
  //   };
  // }

  // Build normalized DD-MM-YYYY for non-partial usage
  const normalized = `${String(day).padStart(2, "0")}-${String(month).padStart(
    2,
    "0"
  )}-${year}`;

  // 👉 For typing in the field: DON'T rewrite what user typed
  if (allowPartial) {
    return { value: cleaned, error: "" };
  }

  // 👉 For final validation: return normalized full year (e.g. 2025)
  return { value: normalized, error: "" };
};

const calculateDueDate = (invoiceDate, terms) => {
  if (!invoiceDate) return "";

  let parsedInvoiceDate;

  if (invoiceDate instanceof Date) {
    parsedInvoiceDate = invoiceDate;
  } else if (typeof invoiceDate === "string") {
    const { value: normalized, error } = normalizeInvoiceDateInput(
      invoiceDate,
      {
        allowPartial: false, // ⛔ only full dates here
      }
    );

    if (error || !normalized) {
      console.error("Invalid invoice date:", invoiceDate);
      return "";
    }

    const parsedValue = parse(normalized, "dd-MM-yyyy", new Date());
    if (isNaN(parsedValue.getTime())) {
      console.error("Invalid invoice date:", invoiceDate);
      return "";
    }

    parsedInvoiceDate = parsedValue;
  } else {
    return "";
  }

  let daysToAdd = 0;
  if (terms && terms !== "due" && !isNaN(parseInt(terms, 10))) {
    daysToAdd = parseInt(terms, 10);
  }

  const updatedDueDate = addDays(parsedInvoiceDate, daysToAdd);
  return format(updatedDueDate, "dd-MM-yyyy");
};

export default function InvoiceForm({ isSidebarExpanded }) {
  const [items, setItems] = useState([
    {
      type: "",
      financialYear:"",
      quantity: 1,
      rate: 0,
      discount: 0,
      tax: 0,
      amount: 0,
    },
  ]);
  const [isInvoiceNumberFetched, setIsInvoiceNumberFetched] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false); // Add this line
  const [gstValue, setGstValue] = useState(false); // Default is false, you can change it to true if needed.
  const [term, setTerm] = useState("");
  const [totalAmount, setTotalAmount] = useState(0); // Total amount before discount
  const [totalDiscount, setTotalDiscount] = useState(0); // Total discount percentage
  const [totalDiscountAmount, setTotalDiscountAmount] = useState(0); // Total discount value
  const [totalAmountAfterDiscount, setTotalAmountAfterDiscount] = useState(0); // Final amount after discount
  const [totalDiscountAmountState, setTotalDiscountAmountState] = useState(0);
  const [isVisitorPopupOpen, setIsVisitorPopupOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [subsidaries, setSubsidaries] = useState([]);
  const [selectedSubsidary, setSelectedSubsidary] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(formatDate(new Date())); // default today DD-MM-YYYY
  const [dueDate, setDueDate] = useState("");
  const [discountType, setdiscountType] = useState("");
  const [services, setServices] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [servicesError, setServicesError] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState(""); // New state for invoice number
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error"); // error by default
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [selectedCompanyType, setSelectedCompanyType] = useState("");
  const [billingAddress, setBillingAddress] = useState(null);
  const [shippingAddress, setShippingAddress] = useState(null);
  // const [subsidiaryId, setSubsidiaryId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  // Add new state near the top of the component:
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientType, setClientType] = useState("");
  const [familyGroups, setFamilyGroups] = useState([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState("");
  const [familyGroupsLoading, setFamilyGroupsLoading] = useState(false);
  const [familyGroupsError, setFamilyGroupsError] = useState("");
  const [isCreateFamilyModalOpen, setIsCreateFamilyModalOpen] = useState(false);
  // Client Management Form States for Modal
  const [contactPersons, setContactPersons] = useState([
    {
      name: "",
      email: "",
      phone: "",
      isPrimary: true,
    },
  ]);
  const [modalSelectedCompanyType, setModalSelectedCompanyType] = useState("");
  const [modalClientName, setModalClientName] = useState("");
  const [modalCompanyName, setModalCompanyName] = useState("");
  const [modalFileNumber, setModalFileNumber] = useState("");
  const [modalSubsidaries, setModalSubsidaries] = useState([]);
  const [modalSelectedSubsidary, setModalSelectedSubsidary] = useState("");
  const [modalPaymentTerm, setModalPaymentTerm] = useState(30);
  const [modalSelectedOptionBalance, setModalSelectedOptionBalance] =
    useState(null);
  const [modalDob, setModalDob] = useState("");
  const [modalDobError, setModalDobError] = useState("");
  const [createFamilyForm, setCreateFamilyForm] = useState({
    family_name: "",
    contact_name: "",
    phone_number: "",
    email: "",
  });
  const createFamilyFirstInputRef = useRef(null);
  const [createFamilyErrors, setCreateFamilyErrors] = useState({});
  const [isCreatingFamily, setIsCreatingFamily] = useState(false);
  const [modalPan, setModalPan] = useState("");
  const [modalOpeningAmount, setModalOpeningAmount] = useState("");
  const [modalAddress, setModalAddress] = useState({
    street: "",
    city: "",
    state: "",
    postalCode: "",
  });
  const [modalErrors, setModalErrors] = useState({});
  const [modalLoading, setModalLoading] = useState(false);
  const [modalIsSaved, setModalIsSaved] = useState(false);
  const [isModalSubmitting, setIsModalSubmitting] = useState(false);
  const [invoiceDateError, setInvoiceDateError] = useState("");
  const familyModalPortalTarget =
    typeof document !== "undefined" ? document.body : null;
   const resetCreateFamilyModal = () => {
    setCreateFamilyForm({
      family_name: "",
      contact_name: "",
      phone_number: "",
      email: "",
    });
    setCreateFamilyErrors({});
    setIsCreatingFamily(false);
  };
  const firstfield = useRef();
  const companyTypes = [
    { value: "individual", label: "Individual / Proprietorship" },
    { value: "huf", label: "HUF" },
    { value: "partnership_firm", label: "Partnership Firm" },
    { value: "private_ltd", label: "Private Limited Company" },
    { value: "limited_company", label: "Limited Company" },
    { value: "llp", label: "Limited Liability Partnership" },
    { value: "aop", label: "Association of Persons" },
    { value: "trust", label: "Trust" },
  ];

  const balanceOptions = [
    { value: "advance", label: "Advance" },
    { value: "pending", label: "Pending" },
    { value: "Financially Clear", label: "Financially Clear" },
  ];
  const getDefaultContact = () => ({
    name: "",
    email: "",
    phone: "",
    isPrimary: true,
    isFamilyPrimaryMapped: false,
  });
  const menuPortalTarget = typeof window !== "undefined" ? document.body : null;
  
  const openCreateFamilyModal = () => {
    document.activeElement?.blur?.();
    resetCreateFamilyModal();
    setIsCreateFamilyModalOpen(true);
  };
  const closeCreateFamilyModal = () => {
    setIsCreateFamilyModalOpen(false);
    resetCreateFamilyModal();
  };
  const discountOptions = [
    { value: "rs", label: "₹" },
    { value: "percentage", label: "%" },
  ];
  const [showEditButton, setShowEditButton] = useState(false);
  const location = useLocation(); // ✅ Correct way to use location
  const invoiceDateInputRef = useRef(null);

  const customStyles = {
    control: (base) => ({
      ...base,
      minWidth: "53px",
      maxWidth: "53px",
      height: "36px",
      padding: "0",
      borderRadius: "6px",
      fontSize: "14px",
      textAlign: "center",
      cursor: "pointer",
      border: "1px solid #e2e8f0",
      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
      "&:hover": {
        borderColor: "#cbd5e0",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      justifyContent: "center",
      padding: "0",
    }),
    singleValue: (base) => ({
      ...base,
      margin: "0",
      color: "#4a5568",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "2px",
      color: "#a0aec0",
      "&:hover": {
        color: "#718096",
      },
    }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (base) => ({
      ...base,
      minWidth: "53px",
      fontSize: "14px",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      borderRadius: "6px",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#edf2f7" : "white",
      color: "#4a5568",
      "&:hover": {
        backgroundColor: "#e2e8f0",
      },
    }),
  };
  const serviceDropdownStyles = {
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  const getModalClientSelectStyles = (hasError = false, portalZIndex = 1500) => ({
    control: (base, state) => ({
      ...base,
      minHeight: 44,
      borderRadius: 12,
      borderColor: hasError ? "#fb7185" : state.isFocused ? "#10b981" : "#e2e8f0",
      boxShadow: state.isFocused
        ? hasError
          ? "0 0 0 2px rgba(244, 63, 94, 0.12)"
          : "0 0 0 2px rgba(16, 185, 129, 0.12)"
        : "none",
      backgroundColor: "#ffffff",
      "&:hover": {
        borderColor: hasError ? "#fb7185" : "#cbd5e1",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 12px",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#94a3b8",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#0f172a",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "#94a3b8",
      "&:hover": {
        color: "#64748b",
      },
    }),
    indicatorSeparator: () => ({ display: "none" }),
    menuPortal: (base) => ({ ...base, zIndex: portalZIndex }),
    menu: (base) => ({
      ...base,
      zIndex: portalZIndex,
      border: "1px solid #e2e8f0",
      borderRadius: 14,
      overflow: "hidden",
      boxShadow: "0 18px 38px rgba(15, 23, 42, 0.16)",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#ecfdf5"
        : state.isFocused
        ? "#f8fafc"
        : "#ffffff",
      color: state.isSelected ? "#065f46" : "#0f172a",
      cursor: "pointer",
    }),
  });

  // Modal styles
  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "min(1152px, calc(100vw - 32px))",
    maxHeight: "92vh",
    bgcolor: "#ffffff",
    boxShadow: "0 30px 80px rgba(15, 23, 42, 0.22)",
    borderRadius: "24px",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    zIndex: 1400,
  };

  // useEffect(() => {
  //   const fetchClients = async () => {
  //     const authKey = localStorage.getItem("authKey");
  //     try {
  //       const response = await axios.get(`${API_URL}/api/get_client_name`, {
  //         headers: {
  //           Authorization: `Bearer ${authKey}`,
  //         },
  //       });

  //       const clientData = response.data.data;

  //       // Set clients
  //       setClients(clientData);

  //       // Set subsidiary ID based on the first client or another condition
  //       if (clientData.length > 0) {
  //         const firstSubsidiaryId = clientData[0].subsidiary_id;
  //         setSubsidiaryId(firstSubsidiaryId);
  //       }

  //       setLoading(false);
  //     } catch (error) {
  //       setError("Failed to fetch clients");
  //       setLoading(false);
  //     }
  //   };

  //   fetchClients();
  // }, []);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axiosInstance.get(`/api/get_client_name`);

        const clientData = response.data.data;
        setClients(clientData);

        const searchParams = new URLSearchParams(location.search);
        const clientId = searchParams.get("client_id");

        if (clientId) {
          const selectedClient = clientData.find(
            (client) => String(client.client_id) === String(clientId)
          );

          if (selectedClient) {
            handleClientChange({
              value: selectedClient.client_id,
              label: selectedClient.client_name,
            });
          }
        } else if (clientData.length > 0) {
          const firstClient = clientData[0];
          handleClientChange({
            value: firstClient.client_id,
            label: firstClient.client_name,
          });
        }
      } catch (error) {
        console.error("❌ Failed to fetch clients:", error);
        setError("Failed to fetch clients");
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, []); // ✅ runs once on mount// ✅ runs once on mount
  useEffect(() => {
    if (clientType === "family group") {
      fetchFamilyGroups();
      return;
    }

    setSelectedFamilyId("");
    setFamilyGroupsError("");
    setIsCreateFamilyModalOpen(false);
  }, [clientType]);

  useEffect(() => {
    if (!isCreateFamilyModalOpen) return;

    // Let portal content mount, then focus the first field reliably.
    const rafId = window.requestAnimationFrame(() => {
      createFamilyFirstInputRef.current?.focus({ preventScroll: true });
    });
    const timer = window.setTimeout(() => {
      createFamilyFirstInputRef.current?.focus({ preventScroll: true });
    }, 120);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timer);
    };
  }, [isCreateFamilyModalOpen]);
   useEffect(() => {
    if (!isCreateFamilyModalOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isCreateFamilyModalOpen]);
  // 🔥 Fetch subsidaries/subsidiaries
  useEffect(() => {

    const fetchSubsidaryAndServices = async () => {
      try {
        const response = await axiosInstance.get(`/api/invoice/metadata`);
        const companyData = response.data.subsidiaries;
        const servicesData = response.data.services;
        
        setSubsidaries(companyData);
        setServices(servicesData);
        setInvoiceNumber(response.data.invoice_number);
      } catch (error) {
        console.error("❌ Failed to fetch subsidaries:", error);
      }
    };

    fetchSubsidaryAndServices();
  }, []);

  // useEffect(() => {
  //   const fetchServices = async () => {
  //     try {
  //       const response = await axiosInstance.get(`/api/get_all_service`);
  //       setServices(response.data.data);
  //       setLoadingServices(false);
  //     } catch (error) {
  //       setServicesError("Failed to fetch services");
  //       setLoadingServices(false);
  //     }
  //   };

  //   fetchServices();
  // }, []);
useEffect(() => {
    const fetchFinancialYears = async () => {
      try {
        const response = await axiosInstance.get(`/api/financial-years`);
        
        setFinancialYears(response.data.data);
        // setLoadingServices(false);
      } catch (error) {
        // setServicesError("Failed to fetch financial years");
        // setLoadingServices(false);
      }
    };

    fetchFinancialYears();
  }, []);
   const clearMappedFamilyContact = (contacts) => {
    const nonFamilyContacts = (contacts || [])
      .filter((contact) => !contact?.isFamilyPrimaryMapped)
      .map((contact) => ({
        ...contact,
        isFamilyPrimaryMapped: false,
      }));

    if (nonFamilyContacts.length === 0) {
      return [getDefaultContact()];
    }

    const hasPrimary = nonFamilyContacts.some((contact) => contact.isPrimary);
    return nonFamilyContacts.map((contact, index) => ({
      ...contact,
      isPrimary: hasPrimary ? contact.isPrimary : index === 0,
    }));
  };
  const fetchFamilyGroups = async (force = false) => {
    if (!force && (familyGroupsLoading || familyGroups.length > 0)) return;
    try {
      setFamilyGroupsLoading(true);
      setFamilyGroupsError("");
      const response = await axiosInstance.get("/api/family_group");
      const families = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data?.result)
        ? response.data.result
        : Array.isArray(response.data)
        ? response.data
        : [];
      setFamilyGroups(families);
    } catch (error) {
      console.error("Error fetching family groups:", error.message);
      setFamilyGroupsError("Failed to load family groups");
    } finally {
      setFamilyGroupsLoading(false);
    }
  };
    const mapFamilyContactToPrimary = (family) => {
    if (!family) return;
    setContactPersons((prevContacts) => {
      const nonFamilyContacts = (prevContacts || [])
        .filter((contact) => !contact?.isFamilyPrimaryMapped)
        .map((contact) => ({
          ...contact,
          isFamilyPrimaryMapped: false,
        }));

      const mappedFamilyPrimary = {
        name: family.contact_name || "",
        email: family.email || "",
        phone: (family.phone_number || "").toString(),
        isPrimary: true,
        isFamilyPrimaryMapped: true,
      };

      const nextContacts = [mappedFamilyPrimary, ...nonFamilyContacts];

      return nextContacts.map((contact, index) => ({
        ...contact,
        isPrimary: index === 0,
      }));
    });
  };
   const handleClientTypeChange = (nextClientType) => {
    setClientType(nextClientType);
    setErrors((prev) => ({ ...prev, clientType: "" }));

    if (nextClientType !== "individual") return;

    setSelectedFamilyId("");
    setFamilyGroupsError("");
    setIsCreateFamilyModalOpen(false);
    setContactPersons((prevContacts) => clearMappedFamilyContact(prevContacts));
  };
  // Function to open the custom modal
  const handleOpenCustomModal = async () => {
    setIsCustomModalOpen(true);

    // Fetch subsidiaries when modal opens
    try {
      setModalLoading(true);
      const response = await axiosInstance.get(
        `/api/get_all_subsidiary?page_status=Active`
      );

      if (response.status === 200) {
        const firms = response.data.data;
        console.log("Fetched Subsidiaries:", firms);

        const firmNames = firms.map((company) => ({
          label: company.Subsidiary_name,
          value: company.subsidiary_id,
          tag_name: company.tag_name,
        }));

        console.log("Processed Options:", firmNames);
        setModalSubsidaries(firmNames);
      } else {
        console.error("Failed to fetch subsidiaries");
      }
    } catch (error) {
      console.error("Error fetching subsidiary data:", error.message);
    } finally {
      setModalLoading(false);
    }
  };
  // Function to close the custom modal
  const handleCloseCustomModal = () => {
    setIsCustomModalOpen(false);
    // Reset all modal form fields
    setModalClientName("");
    setModalCompanyName("");
    setModalFileNumber("");
    setModalSelectedCompanyType("");
    setModalSelectedSubsidary("");
    setModalPaymentTerm(30);
    setModalSelectedOptionBalance(null);
    setModalDob("");
    setModalPan("");
    setModalOpeningAmount("");
    setModalAddress({
      street: "",
      city: "",
      state: "",
      postalCode: "",
    });
    setContactPersons([
      {
        name: "",
        email: "",
        phone: "",
        isPrimary: true,
      },
    ]);
    setSelectedFamilyId("");
    setClientType("individual");
    setModalErrors({});
    setModalIsSaved(false);
    setIsModalSubmitting(false); // Reset submission state
    setModalLoading(false);
  };

  // Modal form validation
  const validateModalForm = () => {
    let isValid = true;
    const newErrors = {};

    // 🔹 Contact Persons Validation
    contactPersons.forEach((contact, i) => {
      if (!contact.name?.trim()) {
        newErrors[`name_${i}`] = "Name is required";
        isValid = false;
      } else if (contact.name.length > 100) {
        newErrors[`name_${i}`] = "Name must not exceed 100 characters";
        isValid = false;
      }

      if (!contact.email?.trim()) {
        newErrors[`email_${i}`] = "Email is required";
        isValid = false;
      } else if (
        !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(contact.email)
      ) {
        newErrors[`email_${i}`] = "Enter a valid email address";
        isValid = false;
      } else if (contact.email.length > 100) {
        newErrors[`email_${i}`] = "Email must not exceed 100 characters";
        isValid = false;
      }

      if (!contact.phone?.trim()) {
        newErrors[`phone_${i}`] = "Phone number is required";
        isValid = false;
      } else if (!/^\d{10}$/.test(contact.phone)) {
        newErrors[`phone_${i}`] = "Phone number must be 10 digits";
        isValid = false;
      }
    });

    // 🔹 Client Name Validation
    if (!modalClientName?.trim()) {
      newErrors.clientName = "Client name is required";
      isValid = false;
    } else if (modalClientName.length > 100) {
      newErrors.clientName = "Client name must not exceed 100 characters";
      isValid = false;
    }

    // 🔹 Company Name Validation
    if (!modalCompanyName?.trim()) {
      newErrors.companyName = "Company name is required";
      isValid = false;
    } else if (modalCompanyName.length > 150) {
      newErrors.companyName = "Company name must not exceed 150 characters";
      isValid = false;
    }

    // 🔹 File Number Validation
    if (!modalFileNumber?.trim()) {
      newErrors.fileNumber = "File number is required";
      isValid = false;
    } else if (modalFileNumber.length > 50) {
      newErrors.fileNumber = "File number must not exceed 50 characters";
      isValid = false;
    }

    // 🔹 Company Type Validation
    if (!modalSelectedCompanyType) {
      newErrors.companyType = "Company type is required";
      isValid = false;
    }

    // 🔹 Subsidiary Validation
    if (!modalSelectedSubsidary) {
      newErrors.subsidiary = "Subsidiary selection is required";
      isValid = false;
    }

    // 🔹 Opening Amount Validation
    if (modalSelectedOptionBalance?.value !== "Financially Clear" && modalSelectedOptionBalance?.value ) {
      
      if (!modalOpeningAmount) {
        newErrors.openingAmount = "Opening amount is required";
        isValid = false;
      } else if (Number(modalOpeningAmount) < 0) {
        newErrors.openingAmount = "Opening amount must be a positive number";
        isValid = false;
      } else if (modalOpeningAmount.toString().length > 15) {
        newErrors.openingAmount = "Opening amount must not exceed 15 digits";
        isValid = false;
      }
    }

    if (!modalDob) {
      newErrors.dob = "Birth date/Date Of Incorporation is required";
      isValid = false;
    } else {
      // Use the birth date normalization function
      const { value: normalized, error } = normalizeBirthDateInput(modalDob, {
        allowPartial: false, // Only accept complete dates for validation
      });

      if (error) {
        newErrors.dob = error;
        isValid = false;
      }
    }

    // 🔹 PAN Validation
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!modalPan?.trim()) {
      newErrors.pan = "PAN number is required";
      isValid = false;
    } else if (!panRegex.test(modalPan)) {
      newErrors.pan = "Invalid PAN format (e.g., ABCDE1234F)";
      isValid = false;
    } else if (modalPan.length > 10) {
      newErrors.pan = "PAN cannot exceed 10 characters";
      isValid = false;
    }

    // 🔹 Address Validation
    if (!modalAddress.street?.trim()) {
      newErrors.street = "Office number is required";
      isValid = false;
    } else if (modalAddress.street.length > 100) {
      newErrors.street = "Office number must not exceed 100 characters";
      isValid = false;
    }

    if (!modalAddress.city?.trim()) {
      newErrors.city = "Name of premises is required";
      isValid = false;
    } else if (modalAddress.city.length > 100) {
      newErrors.city = "Name of premises must not exceed 100 characters";
      isValid = false;
    }

    if (!modalAddress.state?.trim()) {
      newErrors.area = "Area is required";
      isValid = false;
    } else if (modalAddress.state.length > 100) {
      newErrors.area = "Area must not exceed 100 characters";
      isValid = false;
    }

    if (!modalAddress.postalCode) {
      newErrors.postalCode = "Postal code is required";
      isValid = false;
    } else if (!/^\d{6}$/.test(modalAddress.postalCode)) {
      newErrors.postalCode = "Enter a valid 6-digit postal code";
      isValid = false;
    }

    // 🔹 Payment Terms Validation
    if (!String(modalPaymentTerm).trim()) {
      newErrors.paymentTerm = "Payment term is required";
      isValid = false;
    }

    setModalErrors(newErrors);
    return isValid;
  };

  // Handle modal form submission
  const handleModalSubmit = async (e) => {
    e.preventDefault();

    if (!validateModalForm() || modalLoading || modalIsSaved) return;
    if (clientType === "family group" && !selectedFamilyId) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Please select a Family Group");
      setSnackbarOpen(true);
      return;
    }
    setModalLoading(true);
    const selectedFamily = clientType === "family group"
  ? familyGroups.find((family) => family.family_id === selectedFamilyId)
  : null;
    let formattedDate = null;
    if (modalDob) {
      const dateRegex = /^(\d{2})-(\d{2})-(\d{4})$/;
      const match = modalDob.match(dateRegex);
      if (match) {
        const day = match[1];
        const month = match[2];
        const year = match[3];
        formattedDate = `${year}-${month}-${day}`; // Convert to YYYY-MM-DD
      }
    }
    const hasPrimary = contactPersons.some((contact) => contact.isPrimary);
    const nonFamilyContacts = contactPersons.filter(
  (contact) => !contact?.isFamilyPrimaryMapped
);
    const contactsPayload = clientType === "family group"
      ? [
          {
            contact_name: selectedFamily?.contact_name || "",
            contact_email: selectedFamily?.email || "",
            phone_number: (selectedFamily?.phone_number || "").toString(),
            is_primary: true,
          },
          ...nonFamilyContacts.map((contact) => ({
            contact_name: contact.name,
            contact_email: contact.email,
            phone_number: contact.phone,
            is_primary: false,
          })),
        ]
      : contactPersons.map((contact, index) => ({
          contact_name: contact.name,
          contact_email: contact.email,
          phone_number: contact.phone,
          is_primary: hasPrimary ? contact.isPrimary : index === 0,
        }));
    const today = new Date().toISOString().split("T")[0];

    const formData = {
      client_name: modalClientName,
      clients_company_name: modalCompanyName,
      file_number: modalFileNumber,
      company_type: modalSelectedCompanyType || "",
      contacts: contactsPayload, // replace the existing contacts line
      ...(clientType === "family group"
        ? { client_type: "family group", family_id: selectedFamilyId }
        : { client_type: "individual" }),
      sub_id: modalSelectedSubsidary,
      payment_terms: modalPaymentTerm,
      dob: formattedDate,
      pan_number: modalPan,
      office_number: modalAddress.street,
      landmark: modalAddress.city,
      area: modalAddress.state,
      pin: modalAddress.postalCode,
      balance_type: modalSelectedOptionBalance?.label,
      opening_balance: modalOpeningAmount,
      date: formattedDate || today,
    };

    try {
      const response = await axiosInstance.post(`/api/create_client`, formData);

      if (response.status === 200) {
        console.log("Client created successfully:", response.data);
        setSnackbarSeverity("success");
        setSnackbarMessage("Client added successfully!");
        setSnackbarOpen(true);
        setModalIsSaved(true);

        // Extract newly created client
        const createdClient = response.data?.data || response.data;
        const newClientId = createdClient?.client_id;

        // 1. Add the new client into dropdown list immediately
        if (createdClient) {
          setClients((prev) => [
            ...prev,
            {
              client_id: createdClient.client_id,
              client_name: createdClient.client_name,
              company_type: createdClient.company_type,
              terms: createdClient.payment_terms,
              subsidiary_id: createdClient.subsidiary_id,
            },
          ]);
        }

        // 2. Auto-select the new client
        if (newClientId) {
          setSelectedClient(newClientId);
        }

        // 3. Fetch latest invoice for this client
        await fetchClientsAndInvoiceNumber(newClientId);
        // Close modal after successful submission
        setTimeout(() => {
          handleCloseCustomModal();
        }, 2000);
      }
    } catch (error) {
      console.error("Error submitting form:", error);

      // Check if the error is about duplicate PAN
      if (error.response?.data?.error === "PAN number already exists") {
        setModalErrors((prev) => ({
          ...prev,
          pan: "This PAN number is already registered in the system",
        }));
        setSnackbarSeverity("error");
        setSnackbarMessage("PAN number already exists in the system");
      } else {
        // Display backend error message or fallback to generic message
        const backendErrorMessage =
          error.response?.data?.error || error.response?.data?.message;

        setSnackbarSeverity("error");
        setSnackbarMessage(backendErrorMessage);
      }
      setSnackbarOpen(true);
    } finally {
      setModalLoading(false);
      setIsModalSubmitting(false);
    }
  };
  const validateCreateFamilyForm = () => {
    const nextErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!createFamilyForm.family_name.trim()) {
      nextErrors.family_name = "Family Name is required";
    }
    if (!createFamilyForm.contact_name.trim()) {
      nextErrors.contact_name = "Contact Name is required";
    }
    if (!createFamilyForm.phone_number.trim()) {
      nextErrors.phone_number = "Phone Number is required";
    } else if (!/^\d{10}$/.test(createFamilyForm.phone_number.trim())) {
      nextErrors.phone_number = "Phone Number must be 10 digits";
    }
    if (!createFamilyForm.email.trim()) {
      nextErrors.email = "Email Id is required";
    } else if (!emailRegex.test(createFamilyForm.email.trim())) {
      nextErrors.email = "Enter a valid email address";
    }

    setCreateFamilyErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };
  const handleCreateFamily = async () => {
    if (isCreatingFamily || !validateCreateFamilyForm()) return;

    const payload = {
      family_name: createFamilyForm.family_name.trim(),
      contact_name: createFamilyForm.contact_name.trim(),
      phone_number: createFamilyForm.phone_number.trim(),
      email: createFamilyForm.email.trim(),
    };

    try {
      setIsCreatingFamily(true);
      const response = await axiosInstance.post("/api/create_family_group", payload);
      const returnedFamilyId =
        response.data?.family_id ||
        response.data?.data?.family_id ||
        response.data?.result?.family_id ||
        "";

      if (!returnedFamilyId) {
        throw new Error("Family ID missing in response");
      }

      const createdFamily = {
        family_id: returnedFamilyId,
        family_name: payload.family_name,
        contact_name: payload.contact_name,
        phone_number: payload.phone_number,
        email: payload.email,
      };

      setFamilyGroups((prev) => {
        const filtered = prev.filter(
          (family) => family.family_id !== createdFamily.family_id
        );
        return [...filtered, createdFamily];
      });

      setSelectedFamilyId(createdFamily.family_id);
      mapFamilyContactToPrimary(createdFamily);
      setSnackbarSeverity("success");
      setSnackbarMessage(response.data?.message || "Family Group added successfully");
      setSnackbarOpen(true);
      closeCreateFamilyModal();
    } catch (error) {
      console.error("Error creating family group:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to create Family Group"
      );
      setSnackbarOpen(true);
    } finally {
      setIsCreatingFamily(false);
    }
  };
  // Function to refresh clients and get invoice number
  const fetchClientsAndInvoiceNumber = async (newClientId) => {
    try {
      // Fetch updated clients list
      const clientsResponse = await axiosInstance.get(`/api/get_client_name`);

      if (clientsResponse.status === 200) {
        setClients(clientsResponse.data.data);
      }

      // ❌ Remove invoice number fetching from here
      // Invoice number should only be fetched once on initial load
    } catch (error) {
      console.error("Error fetching updated data:", error);
    }
  };

  // Modal form handlers
  const handleModalAddContactPerson = () => {
    setContactPersons([
      ...contactPersons,
      { name: "", email: "", phone: "", isPrimary: false },
    ]);
  };

  const handleModalContactChange = (index, field, value) => {
    const updatedContacts = [...contactPersons];
    updatedContacts[index][field] = value;
    setContactPersons(updatedContacts);

    // Clear error when user starts typing
    setModalErrors((prevErrors) => ({
      ...prevErrors,
      [`contact_${index}`]: value.trim()
        ? ""
        : prevErrors?.[`contact_${index}`],
    }));
  };

  const handleModalRemoveContactPerson = (index) => {
    const updatedContacts = contactPersons.filter((_, i) => i !== index);
    setContactPersons(updatedContacts);
  };

  const handleModalPrimaryChange = (selectedIndex) => {
    setContactPersons((prevContacts) =>
      prevContacts.map((contact, index) => ({
        ...contact,
        isPrimary: index === selectedIndex,
      }))
    );
  };

  const handleModalAddressChange = (field, value) => {
    setModalAddress((prevAddress) => ({
      ...prevAddress,
      [field]: value,
    }));

    // Clear address error
    setModalErrors((prevErrors) => ({
      ...prevErrors,
      address: Object.values({ ...modalAddress, [field]: value }).every((val) =>
        val.trim()
      )
        ? ""
        : prevErrors.address,
    }));
  };

  const handleModalBalanceChange = (selected) => {
    setModalSelectedOptionBalance(selected);
    setModalErrors((prev) => ({
                            ...prev,
                            openingAmount: "",
                          }));
  };

  const handleModalPanChange = (e) => {
    const value = e.target.value.toUpperCase();
    setModalPan(value);

    if (modalErrors.pan) {
      setModalErrors((prevErrors) => ({ ...prevErrors, pan: "" }));
    }
  };

  const handleFetchValue = (newClient) => {
    console.log("🎯 Received new client:", newClient);

    // Update clients state immediately
    setClients((prevClients) => [
      ...prevClients,
      { client_id: newClient.value, client_name: newClient.label },
    ]);

    // Set the newly added client as selected
    setSelectedClient(newClient.value);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    
    // Update the field value for the selected item
    updatedItems[index][field] = value;

    // When service type is changed
    if (field === "type") {
      const selectedService = services.find(
        (service) => service.service_name === value
      );

      if (selectedService) {
        updatedItems[index].rate = parseFloat(selectedService.rate);

        const client = clients.find(
          (client) => client.client_id === selectedClient
        ); // Ensure selectedClient is set

        if (client) {
          if (client.has_gst) {
            updatedItems[index].tax = parseFloat(selectedService.gst_rate);
            const gstAmount =
              (parseFloat(selectedService.rate) *
                parseFloat(selectedService.gst_rate)) /
              100;
            updatedItems[index].gstAmount = gstAmount;
            updatedItems[index].totalAmount =
              parseFloat(selectedService.rate) + gstAmount;
          } else {
            // If no GST, set gstAmount to 0 and totalAmount equal to rate
            updatedItems[index].tax = 0;
            updatedItems[index].gstAmount = 0;
            updatedItems[index].totalAmount = parseFloat(selectedService.rate);
          }
        }
      }
    }

    if (["quantity", "rate", "discount"].includes(field)) {
      updateAmounts(index, updatedItems);
    }
    
    setItems(updatedItems); // Set the updated items to state
  };

  const updateAmounts = (index, updatedItems) => {
    const baseAmount = updatedItems[index].quantity * updatedItems[index].rate;

    const discountAmount = (baseAmount * updatedItems[index].discount) / 100;

    const amountAfterDiscount = baseAmount - discountAmount;

    const gstAmount = (amountAfterDiscount * updatedItems[index].tax) / 100;

    // Update the final amount after applying discount and adding GST
    updatedItems[index].amount = amountAfterDiscount + gstAmount;
  };

  const getAvailableServices = (currentIndex) => {
    const selectedServiceNames = items
      .filter((_, i) => i !== currentIndex)
      .map((item) => item.type);

    return services.filter(
      (service) => !selectedServiceNames.includes(service.service_name)
    );
  };

  const handleInvoiceDateChange = (e) => {
    const selectedDate = e.target.value;
    setInvoiceDate(selectedDate);

    const date = new Date(selectedDate);

    if (paymentTerms === "due") {
      setDueDate(format(date, "dd MMM yyyy"));
    } else if (paymentTerms && paymentTerms.startsWith("Days")) {
      const daysToAdd = parseInt(paymentTerms.replace("Days", ""), 10);
      const calculatedDueDate = addDays(date, daysToAdd);
      setDueDate(format(calculatedDueDate, "dd MMM yyyy"));
    }
  };

  // const handleClientChange = async (selectedOption) => {
  //   if (!selectedOption) return;

  //   const selectedClientId = selectedOption.value;

  //   if (selectedClientId === "add_new") {
  //     setIsPopupOpen(true); // Open popup to add new client
  //     return;
  //   }

  //   const client = clients.find((c) => c.client_id === selectedClientId);
  //   if (!client) return;

  //   setSelectedClient(selectedClientId);
  //   setSelectedCompanyType(client.company_type);
  //   setPaymentTerms(client.terms === 0 ? "due" : `${client.terms}`);

  //   // ✅ Set Subsidiary ID from the matched client
  //   setSubsidiaryId(client.subsidiary_id);

  //   setBillingAddress({
  //     company: client.clients_company_name || "N/A",
  //     area: client.billing_area || "N/A",
  //     landmark: client.billing_landmark || "N/A",
  //     office: client.billing_office_number || "N/A",
  //     pin: client.billing_pin_code || "N/A",
  //     phone: client.contact_number || "N/A",
  //   });

  //   setShippingAddress({
  //     company: client.shipping_company || "N/A",
  //     address: client.shipping_address || "N/A",
  //     phone: client.contact_number || "N/A",
  //   });

  //   const currentDate = new Date();
  //   setInvoiceDate(formatDate(currentDate));
  //   setDueDate(calculateDueDate(currentDate, client.terms));

  //   try {
  //     const authKey = localStorage.getItem("authKey");
  //     if (!authKey) {
  //       console.error("No authorization key found.");
  //       return;
  //     }

  //     const response = await axios.get(`${API_URL}/api/get_invoice_number`, {
  //       headers: { Authorization: `Bearer ${authKey}` },
  //     });

  //     setInvoiceNumber(response.data?.[0]?.invoice_number || "");

  //     setServices(
  //       services.map((service) => ({
  //         ...service,
  //         gstAmount: client.has_gst
  //           ? (parseFloat(service.rate) * parseFloat(service.gst_rate)) / 100
  //           : 0,
  //         totalAmount:
  //           parseFloat(service.rate) +
  //           (client.has_gst
  //             ? (parseFloat(service.rate) * parseFloat(service.gst_rate)) / 100
  //             : 0),
  //       }))
  //     );
  //   } catch (error) {
  //     console.error("Error fetching invoice number:", error);
  //   }
  // };

  // 🔥 Handle Company Selection
  const handleCompanyChange = (selectedOption) => {
    if (!selectedOption) return;
    setSelectedSubsidary(selectedOption.value);
    
  };

  const handleClientChange = async (selectedOption) => {
    if (!selectedOption) return;

    const selectedClientId = selectedOption.value;

    if (selectedClientId === "add_new") {
      setIsPopupOpen(true);
      return;
    }

    const client = clients.find((c) => c.client_id === selectedClientId);
    ;
    if (!client) return;

    // ✅ Check Subsidiary Status
    const subsidiaryStatus =
      client.subsidiary_info?.[0]?.subsidiary_status ?? null;
    console.log("🚩 Subsidiary Status:", subsidiaryStatus);

    // 🟡 Show Edit if subsidiary status is 1 or info is missing
    if (subsidiaryStatus === 1 || !client.subsidiary_info) {
      console.warn("⚠️ Inactive or Missing Subsidiary. Showing Edit button.");
      setShowEditButton(true);
    } else {
      setShowEditButton(false);
    }

    if (!invoiceDate) {
      const todayStr = formatDate(new Date());
      setInvoiceDate(todayStr);
      const termsStr = client.terms === 0 ? "due" : `${client.terms}`;
      const newDue = calculateDueDate(todayStr, termsStr);
      setDueDate(newDue);
    } else {
      // If user already has a date, just refresh due date with new terms
      const termsStr = client.terms === 0 ? "due" : `${client.terms}`;
      const newDue = calculateDueDate(invoiceDate, termsStr);
      setDueDate(newDue);
    }
    setSelectedSubsidary(client.subsidiary_id); // Set company based on client's subsidiary
    // ✅ Set client-related state
    setSelectedClient(selectedClientId);
    setSelectedCompanyType(client.company_type);
    setPaymentTerms(client.terms === 0 ? "due" : `${client.terms}`);
    // setSubsidiaryId(client.subsidiary_id);

    // ✅ Shipping Address
    setShippingAddress({
      company: client.clients_company_name || "N/A",
      area: client.billing_area || "N/A",
      landmark: client.billing_landmark || "N/A",
      office: client.billing_office_number || "N/A",
      pin: client.billing_pin_code || "N/A",
      phone: client.contact_number || "N/A",
    });

    // ✅ Billing Address
    setBillingAddress({
      company: client.clients_company_name || "N/A",
      area: client.billing_area || "N/A",
      landmark: client.billing_landmark || "N/A",
      office: client.billing_office_number || "N/A",
      pin: client.billing_pin_code || "N/A",
      phone: client.contact_number || "N/A",
    });

    //  If you want, you *can* force due date refresh using current invoiceDate:
    if (invoiceDate) {
      setDueDate(
        calculateDueDate(
          invoiceDate,
          client.terms === 0 ? "due" : `${client.terms}`
        )
      );
    }

    // if (!isInvoiceNumberFetched) {
    //   try {
    //     const response = await axiosInstance.get(`/api/get_invoice_number`);
    //     setInvoiceNumber(response.data?.[0]?.invoice_number || "");
    //     setIsInvoiceNumberFetched(true); // Mark as fetched
    //   } catch (error) {
    //     console.error("Error fetching invoice number:", error);
    //   }
    // }
  };

  // const handleAddNewClient = async (newClient) => {
  //   setIsPopupOpen(false); // Close the popup first

  //   if (newClient) {
  //     setClients((prevClients) => [...prevClients, newClient]);
  //     setSelectedClient(newClient);

  //     // Update formData with the new client's details
  //     setFormData((prevFormData) => ({
  //       ...prevFormData,
  //       CustomerClient: newClient.label, // Assuming this follows the company logic
  //       CustomerClientID: newClient.value,
  //     }));

  //     setSnackbarMessage("New client added successfully!");
  //     setSnackbarOpen(true);
  //   }
  // };

  useEffect(() => {
    // If date is empty or has error, don't calculate
    if (!invoiceDate || !paymentTerms || invoiceDateError) {
      setDueDate("");
      return;
    }

    // Only recalc when date *looks* complete: D-M-YY / D-M-YYYY etc.
    if (!FULL_DATE_REGEX.test(invoiceDate)) {
      return;
    }

    const newDue = calculateDueDate(invoiceDate, paymentTerms);
    setDueDate(newDue);
  }, [invoiceDate, paymentTerms, invoiceDateError]);

  const handleInputChange = (field, value) => {
    switch (field) {
      case "selectedClient":
        setSelectedClient(value);
        break;

      case "invoiceNumber":
        setInvoiceNumber(value);
        break;
      case "invoiceDate": {
        if (!value) {
          setInvoiceDate("");
          setInvoiceDateError("Invoice Date is required");
          setDueDate("");
          return;
        }

        // Store cursor position before state update
        const input = invoiceDateInputRef.current;
        const cursorPosition = input ? input.selectionStart : 0;
        const previousValue = invoiceDate;

        // Check if user is deleting (backspace/delete)
        const isDeleting = value.length < previousValue.length;

        // Auto-insert hyphens only when typing (not deleting)
        let autoFormatted = value.replace(/[^0-9]/g, ""); // Remove non-digits

        if (!isDeleting) {
          if (autoFormatted.length >= 2) {
            autoFormatted =
              autoFormatted.slice(0, 2) + "-" + autoFormatted.slice(2);
          }
          if (autoFormatted.length >= 5) {
            autoFormatted =
              autoFormatted.slice(0, 5) + "-" + autoFormatted.slice(5);
          }
        } else {
          // When deleting, just remove hyphens and let user delete freely
          autoFormatted = value.replace(/[^0-9-]/g, "");
        }

        // Limit to DD-MM-YYYY format (10 chars)
        autoFormatted = autoFormatted.slice(0, 10);

        const { value: cleaned, error } = normalizeInvoiceDateInput(
          autoFormatted,
          {
            allowPartial: true,
          }
        );

        setInvoiceDate(cleaned);
        setInvoiceDateError(error);

        // Restore cursor position after state update
        requestAnimationFrame(() => {
          if (input && cursorPosition !== null) {
            let newPosition = cursorPosition;
            if (!isDeleting) {
              // Adjust cursor if hyphens were added during typing
              if (cursorPosition === 2 && autoFormatted.length > 2)
                newPosition = 3;
              if (cursorPosition === 5 && autoFormatted.length > 5)
                newPosition = 6;
            }
            input.setSelectionRange(newPosition, newPosition);
          }
        });
        break;
      }

      case "paymentTerms":
        setPaymentTerms(value);
        // ❌ Do NOT recalc dueDate here, let useEffect handle it
        break;

      case "dueDate":
        setDueDate(value);
        break;

      default:
        break;
    }
  };

  const addNewRow = () => {
    setItems([
      ...items,
      {
        type: "",
        financialYear: "",
        quantity: 1,
        rate: 0,
        discount: 0,
        tax: "",
        amount: 0,
      },
    ]);
  };
  const handleBack = () => {
    window.history.back();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const invalidItems = items.some(
      (item) => !item.type || !item.financialYear || item.quantity <= 0 || item.rate <= 0
    );

    if (invalidItems) {
      setSnackbarSeverity("error");
      setSnackbarMessage(
        "All items must have a valid type,financial year,  quantity, and rate!"
      );
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsSubmitting(true); // Start loading state
      const termValue = paymentTerms ? paymentTerms.replace(/\D/g, "") : "";

      const subtotal = items.reduce(
        (acc, item) => acc + (parseFloat(item.amount) || 0),
        0
      );

      // Convert DD-MM-YYYY to YYYY-MM-DD for backend
      const convertToBackendFormat = (dateStr) => {
        if (!dateStr) return "";
        const match = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (!match) return "";
        return `${match[3]}-${match[2]}-${match[1]}`; // YYYY-MM-DD
      };

      // Convert dates to backend format
      const formattedInvoiceDate = convertToBackendFormat(invoiceDate);
      const formattedDueDate = convertToBackendFormat(dueDate);

      const invoiceData = {
        client_id: selectedClient,
        subsidiary_id: selectedSubsidary,
        invoice_number: invoiceNumber,
        subtotal: subtotal.toFixed(2),
        discount_type: discountType,
        invoice_date: formattedInvoiceDate,
        dueDate: formattedDueDate,
        total_amount: Number(totalAmountAfterDiscount).toFixed(2),
        terms: termValue,
        companyTypes: selectedCompanyType,
        overall_disount: totalDiscount,
        item_table: items.map((item) => ({
          service_id: item.serviceId,
          fy_id: item.financialYear,
          quantity: item.quantity,
          discount: item.discount,
          amount: item.amount,
          rate: item.rate,
          tax: item.tax,
        })),
      };
      
      const response = await axiosInstance.post(
        `/api/create_invoice`,
        invoiceData
      );

      if (response.status === 200) {
        setIsSubmitting(true); // Keep button disabled
        setSnackbarSeverity("success");
        setSnackbarMessage("Invoice created successfully!");
        setSnackbarOpen(true);

        // Disable all form inputs
        const form = e.target.closest("form");
        if (form) {
          const inputs = form.querySelectorAll("input, select, button");
          inputs.forEach((input) => (input.disabled = true));
        }

        // Navigate after delay, keep button disabled
        setTimeout(() => {
          navigate("/invoice-management");
        }, 2000);
      } else {
        throw new Error("Error creating invoice");
      }
    } catch (error) {
      console.error("Error creating invoice:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage(
        error.response?.data?.message ||
        "Invoice exceeds the maximum limit! Please reduce the amount or adjust your invoice"
      );
      setSnackbarOpen(true);
      setIsSubmitting(false); // Only reset on error
    }
  };

  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const validateForm = () => {
    const validationErrors = {};
    if (!selectedClient)
      validationErrors.selectedClient = "Client is required.";
    if (!invoiceNumber)
      validationErrors.invoiceNumber = "Invoice Number is required.";
    if (!invoiceDate)
      validationErrors.invoiceDate = "Invoice Date is required.";
    if (!paymentTerms)
      validationErrors.paymentTerms = "Payment Terms are required.";
    if (!selectedCompanyType)
      validationErrors.selectedCompanyType = "Company Type is required."; // New validation

    return validationErrors;
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  // const updateTotals = () => {
  //   let amount = 0;
  //   let discount = 0;

  //   // Calculate individual item amounts and update only if necessary
  //   const updatedItems = items.map((item) => {
  //     const baseAmount = item.quantity * item.rate;
  //     const itemDiscount = (baseAmount * item.discount) / 100;
  //     const amountAfterItemDiscount = baseAmount - itemDiscount;
  //     const gstAmount = (amountAfterItemDiscount * item.tax) / 100;

  //     discount += itemDiscount;
  //     amount += amountAfterItemDiscount + gstAmount;

  //     return {
  //       ...item,
  //       amount: amountAfterItemDiscount + gstAmount, // Update item amount
  //     };
  //   });

  //   const totalDiscountAmount = (amount * totalDiscount) / 100;
  //   const finalAmountAfterDiscount = amount - totalDiscountAmount;

  //   if (amount !== totalAmount) {
  //     setTotalAmount(amount); // Update total amount
  //   }

  //   if (totalDiscountAmount !== totalDiscountAmountState) {
  //     // Compare with the state
  //     setTotalDiscountAmountState(totalDiscountAmount); // Update total discount amount
  //   }

  //   if (finalAmountAfterDiscount !== totalAmountAfterDiscount) {
  //     setTotalAmountAfterDiscount(finalAmountAfterDiscount); // Update final amount after discount
  //   }

  //   // Only update items if necessary (check if the amounts changed)
  //   if (JSON.stringify(updatedItems) !== JSON.stringify(items)) {
  //     setItems(updatedItems); // Update items with recalculated amounts
  //   }
  // };
  const updateTotals = () => {
    let amount = 0;
    let discount = 0;

    // Calculate individual item amounts and update only if necessary
    const updatedItems = items.map((item) => {
      const baseAmount = item.quantity * item.rate;
      const itemDiscount = (baseAmount * item.discount) / 100;
      const amountAfterItemDiscount = baseAmount - itemDiscount;
      const gstAmount = (amountAfterItemDiscount * item.tax) / 100;

      discount += itemDiscount;
      amount += amountAfterItemDiscount + gstAmount;

      return {
        ...item,
        amount: amountAfterItemDiscount + gstAmount,
      };
    });

    // Calculate total discount based on discount type
    let totalDiscountAmount = 0;

    if (discountType === "rs") {
      totalDiscountAmount = totalDiscount;
    } else if (discountType === "percentage") {
      totalDiscountAmount = (amount * totalDiscount) / 100;
    }

    // Ensure discount does not exceed total amount
    totalDiscountAmount = Math.min(totalDiscountAmount, amount);

    const finalAmountAfterDiscount = amount - totalDiscountAmount;

    // Update states if values changed
    if (amount !== totalAmount) {
      setTotalAmount(amount);
    }

    if (totalDiscountAmount !== totalDiscountAmountState) {
      setTotalDiscountAmountState(totalDiscountAmount);
    }

    if (finalAmountAfterDiscount !== totalAmountAfterDiscount) {
      setTotalAmountAfterDiscount(finalAmountAfterDiscount);
    }

    // Only update items if necessary
    if (JSON.stringify(updatedItems) !== JSON.stringify(items)) {
      setItems(updatedItems);
    }
  };

  useEffect(() => {
    updateTotals();
  }, [totalDiscount, discountType, items]);

  const handleServiceSelection = (index, selectedOption) => {
    if (selectedOption.value === "add_new_Visitor") {
      setIsVisitorPopupOpen(true); // Open the visitor modal
      return;
    }

    handleItemChange(index, "type", selectedOption.label);
    handleItemChange(index, "serviceId", selectedOption.value);
  };
const handleFYSelection = (index, selectedOption) => {
    
    handleItemChange(index, "financialYear", selectedOption.value);
  };
  useEffect(() => {
    return () => {
      // Cleanup function
      setIsSubmitting(false);
    };
  }, []);
  // const handleAddNewService = (newService) => {
  //   const formattedService = {
  //     value: newService.service_id,
  //     label: newService.service_name,
  //   };

  //   setServices((prevServices) => {
  //     const updatedServices = [...prevServices, formattedService];
  //     return updatedServices; // Ensure a new array is set for re-rendering
  //   });

  //   handleServiceSelection(selectedIndex, formattedService); // selectedIndex should track which dropdown was used
  //   setIsPopupOpen(false); // Close the modal
  // };
  useEffect(() => { }, [services]); // Re-run whenever `services` changes

  // const getMenuItems = (clients) => {
  //   return [
  //     ...clients.map((client) => ({
  //       value: client.client_id,
  //       label: client.client_name,
  //       subsidiaryId: client.subsidiary_id,
  //     })),
  //     { value: "add_new", label: "➕ Add New Client" },
  //   ];
  // };

  // const handleClientNavigation = (clientId) => {
  //   if (!clientId) {
  //     console.warn("❌ No Client ID provided.");
  //     return;
  //   }

  //   console.log("🆔 Navigating with Client ID:", clientId);

  //   // Example: Redirecting or performing an action with the ID
  //   navigate(`/edit-client-management/${clientId}`);
  // };

  const openModal = () => setIsPopupOpen(true);
  const closeModal = () => setIsPopupOpen(false);

  const handleClientNavigation = (clientId) => {
    if (!clientId) {
      console.warn("❌ No Client ID provided.");
      return;
    }

    console.log("🆔 Navigating with Client ID:", clientId);
    navigate(`/edit-client-management/${clientId}?redirect=invoice-form`);
  };

  const handleInputClickzeo = (e) => {
    e.target.select();
  };

  const handleAddNewService = () => {
    setIsModalOpen(true); // Open the modal
  };

  // const handleSaveService = (newService) => {
  //   console.log("📥 Received New Service from Modal:", newService);

  //   setServices((prevServices) => [...prevServices, newService]); // ✅ Add to service list

  //   setTimeout(() => {
  //     setItems((prevItems) => {
  //       const updatedItems = [...prevItems];

  //       const lastIndex = updatedItems.length - 1; // Ensure correct item selection

  //       if (lastIndex >= 0) {
  //         updatedItems[lastIndex].type = newService.service_name;
  //         updatedItems[lastIndex].rate = parseFloat(newService.selling_cost) || 0;
  //         updatedItems[lastIndex].type = newService.service_name; // ✅ Auto-fill service name

  //         // ✅ Fetch client details
  //         const client = clients.find((client) => client.client_id === selectedClient);

  //         if (client && client.has_gst) {
  //           updatedItems[lastIndex].tax = parseFloat(newService.gst_rate) || 0;
  //         } else {
  //           updatedItems[lastIndex].tax = 0; // ❌ No GST if client doesn't have it
  //         }

  //         updateAmounts(lastIndex, updatedItems); // ✅ Recalculate amounts
  //       }

  //       return updatedItems;
  //     });
  //   }, 100); // Delay to ensure state updates
  // };

  const handleSaveService = (newService) => {
    console.log("📥 Received New Service from Modal:", newService);

    setServices((prevServices) => [...prevServices, newService]); // ✅ Add new service

    setItems((prevItems) => {
      const updatedItems = [...prevItems];
      const lastIndex = updatedItems.length - 1;

      if (lastIndex >= 0) {
        updatedItems[lastIndex].type = newService.service_name; // ✅ Auto-fill service name
        updatedItems[lastIndex].serviceId = newService.service_id; // ✅ Ensure ID is set
        updatedItems[lastIndex].rate = parseFloat(newService.selling_cost) || 0;

        // ✅ Fetch client details
        const client = clients.find(
          (client) => client.client_id === selectedClient
        );

        if (client && client.has_gst) {
          updatedItems[lastIndex].tax = parseFloat(newService.gst_rate) || 0;
          updatedItems[lastIndex].gstAmount =
            (updatedItems[lastIndex].rate * updatedItems[lastIndex].tax) / 100;
          updatedItems[lastIndex].totalAmount =
            updatedItems[lastIndex].rate + updatedItems[lastIndex].gstAmount;
        } else {
          updatedItems[lastIndex].tax = 0; // ❌ No GST if client doesn't have it
          updatedItems[lastIndex].gstAmount = 0;
          updatedItems[lastIndex].totalAmount = updatedItems[lastIndex].rate;
        }

        updateAmounts(lastIndex, updatedItems); // ✅ Recalculate amounts
      }

      return updatedItems;
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000); // 3 seconds delay

    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={`table-container invoice-form-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content form-main-body invoice-form-content">
        <div className="invoice-page-heading">
          <h1 className="form-title">New Invoice Management</h1>
          <p className="invoice-page-subtitle">
            Create a new invoice and add billable service items.
          </p>
        </div>
        <div className="invoice-form-card">
          <div className="form-body-invoice invoice-form-body">
          <form onSubmit={handleSubmit} className="form-box invoice-form-box">
            <div className="form-body-container invoice-form-sections">
              <section className="invoice-form-section">
                <div className="invoice-section-header">
                  <div>
                    <h2 className="invoice-section-title">Invoice Details</h2>
                    <p className="invoice-section-description">
                      Select the client and subsidiary, then review the linked
                      company details.
                    </p>
                  </div>
                </div>
              <div className="invoice-primary-grid">
                {/* 🔷 Client Dropdown */}
                <div className="form-group invoice-client-field">
                  <label className="form-label-invoice">
                    Client<span className="required">*</span>
                  </label>

                  {isLoading ? (
                    <div className="loader-container">
                      <ClipLoader color="#007bff" size={30} /> {/* Spinner */}
                      <p>Fetching Clients...</p>
                    </div>
                  ) : (
                    <>
                      <div className="invoice-client-select-row">
                        <div className="invoice-client-select">
                      <Select
                        value={
                          selectedClient
                            ? {
                              label:
                                clients.find(
                                  (client) =>
                                    client.client_id === selectedClient
                                )?.client_name || "Select Client",
                              value: selectedClient,
                            }
                            : null
                        }
                        onChange={handleClientChange}
                        options={clients.map((client) => ({
                          label: client.client_name,
                          value: client.client_id,
                        }))}
                        className={`select-field-invoice ${errors.selectedClient ? "input-error" : ""
                          }`}
                        /** 🔥 FIX: This overrides react-select dropdown container */
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 1200 }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1200,
                            position: "absolute",
                          }),
                          container: (base) => ({
                            ...base,
                            zIndex: 1,
                            position: "relative",
                          }),
                        }}
                        menuPortalTarget={document.body} // IMPORTANT
                        placeholder="Select Client"
                      />
                        </div>
                        <div
                          className="new-button d-flex justify-content-center invoice-new-button"
                          onClick={handleOpenCustomModal}
                        >
                          <span className="new-icon">
                            <LuPlus />
                          </span>
                          <span>New</span>
                        </div>
                      </div>

                      {errors.selectedClient && (
                        <p className="error-message">{errors.selectedClient}</p>
                      )}
                    </>
                  )}
                </div>

                {/* 🔷 Company Dropdown */}
                <div className="form-group">
                  <label className="form-label-invoice">
                    Subsidary<span className="required">*</span>
                  </label>

                  {isLoading ? (
                    <div className="loader-container">
                      <ClipLoader color="#007bff" size={30} /> {/* Spinner */}
                      <p>Fetching Subsidaries...</p>
                    </div>
                  ) : (
                    <>
                      <Select
                        value={
                          selectedSubsidary
                            ? {
                              label:
                                subsidaries.find(
                                  (company) =>
                                    company.subsidiary_id === selectedSubsidary
                                )?.subsidiary_name || "Select Subsidary",
                              value: selectedSubsidary,
                            }
                            : null
                        }
                        onChange={handleCompanyChange}
                        options={subsidaries.map((company) => ({
                          label: company.subsidiary_name,
                          value: company.subsidiary_id,
                        }))}
                        className={`select-field-invoice ${errors.selectedSubsidary ? "input-error" : ""
                          }`}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 1200 }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1200,
                            position: "absolute",
                          }),
                          container: (base) => ({
                            ...base,
                            zIndex: 1,
                            position: "relative",
                          }),
                        }}
                        menuPortalTarget={document.body}
                        placeholder="Select Subsidary"
                      />

                      {errors.selectedSubsidary && (
                        <p className="error-message">{errors.selectedSubsidary}</p>
                      )}
                    </>
                  )}
                  {/* <div
                    className="new-button d-flex justify-content-center"
                    onClick={handleOpenCompanyModal}
                  >
                    <span className="new-icon">
                      <LuPlus />
                    </span>
                    <span>New</span>
                  </div> */}
                </div>
              </div>

              {billingAddress && shippingAddress && (
                <div className="address-container invoice-address-grid">
                  {/* Billing Address */}
                  <div className="address-block">
                    <h3 className="address-title">Billing Address </h3>
                    <div className="address-details">
                      <strong>{billingAddress.company}</strong>

                      <p>{billingAddress.office}</p>
                      <p>{billingAddress.landmark}</p>
                      <p>{billingAddress.area}</p>
                      <p>{billingAddress.pin}</p>
                      <p>
                        <strong>Phone:</strong> {billingAddress.phone}
                      </p>
                    </div>
                  </div>

                  {/* Shipping Address */}
                  <div className="address-block">
                    <h3 className="address-title">Shipping Address </h3>
                    <div className="address-details">
                      <div>
                        {showEditButton ? (
                          <button
                            onClick={() =>
                              handleClientNavigation(selectedClient)
                            }
                            className="edit-button-invoice"
                          >
                            Edit Subsidiary Company
                          </button>
                        ) : (
                          <strong>{shippingAddress.company}</strong>
                        )}
                      </div>

                      <p>{shippingAddress.address}</p>
                      <p>
                        <p>{billingAddress.office}</p>
                        <p>{billingAddress.landmark}</p>
                        <p>{billingAddress.area}</p>
                        <p>{billingAddress.pin}</p>
                        <strong>Phone:</strong> {shippingAddress.phone}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="invoice-secondary-grid" style={{ marginTop: "16px" }}>
              <div className="form-group">
                <label className="form-label-invoice">Company Type</label>
                <div className="form-inputs">
                  <input
                    type="text"
                    className={`input-fieldsss ${errors.selectedCompanyType ? "input-error" : ""
                      }`}
                    value={selectedCompanyType || ""}
                    readOnly
                    placeholder="Company Type" // Placeholder for the input
                  />
                  {errors.selectedCompanyType && (
                    <p className="error-message">
                      {errors.selectedCompanyType}
                    </p>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label-invoice">
                  Invoice Number
                  <span className="required">*</span>
                </label>
                <input
                  type="text" // Janvi Made change here
                  value={invoiceNumber}
                  readOnly // Make the input field non-editable
                  placeholder="Invoice Number will be auto-filled"
                  className={`input-field input-autofill ${errors.invoiceNumber ? "input-error" : ""
                    }`}
                />
                {errors.invoiceNumber && (
                  <p className="error-message">{errors.invoiceNumber}</p>
                )}
              </div>
              </div>
              </section>

              <section className="invoice-form-section">
                <div className="invoice-section-header">
                  <div>
                    <h2 className="invoice-section-title">
                      Billing Period &amp; Terms
                    </h2>
                    <p className="invoice-section-description">
                      Set the invoice date, payment terms and due date.
                    </p>
                  </div>
                </div>
                <div className="invoice-billing-grid">
                  <div className="form-group">
                    <label className="form-label-invoice">
                      Invoice Date<span className="required">*</span>
                    </label>
                    <div className="date-input-container">
                      <input
                        ref={invoiceDateInputRef}
                        type="text"
                        value={invoiceDate || ""}
                        onChange={(e) =>
                          handleInputChange("invoiceDate", e.target.value)
                        }
                        placeholder="Enter the Date as DD-MM-YYYY"
                        maxLength={10}
                        className={`input-field-datepicker ${errors.invoiceDate || invoiceDateError
                          ? "input-error"
                          : ""
                          }`}
                      />
                      <div className="date-example-text">(DD-MM-YYYY)</div>
                    </div>

                    {invoiceDateError && (
                      <p className="error-message">{invoiceDateError}</p>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label-invoice terms-label">
                      Terms<span className="required">*</span>
                    </label>
                    <div className="invoice-terms-input">
                      <input
                        type="text"
                        value={paymentTerms || ""}
                        onChange={(e) =>
                          handleInputChange("paymentTerms", e.target.value)
                        }
                        placeholder="Enter days" // ✨ Shorter placeholder
                        className={`input-field ${errors.paymentTerms ? "input-error" : ""
                          }`}
                      />
                      <span className="invoice-terms-suffix">Days</span>
                    </div>
                    {errors.paymentTerms && (
                      <p className="error-message">{errors.paymentTerms}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label-invoice">
                     Due Date<span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={dueDate || ""} // Display empty string initially
                      onChange={(e) =>
                        handleInputChange("dueDate", e.target.value)
                      } // Handle changes if any
                      placeholder="Enter Due Date"
                      className={`input-field ${errors.dueDate ? "input-error" : ""
                        }`}
                      readOnly // Make the field non-editable
                    />
                    {errors.dueDate && (
                      <p className="error-message">{errors.dueDate}</p> // Show error message if there is one
                    )}
                  </div>
                </div>
              </section>
              <section className="invoice-form-section invoice-form-section-last">
                <div className="invoice-section-header">
                  <div>
                    <h2 className="invoice-section-title">Item Table</h2>
                    <p className="invoice-section-description">
                      Add service-wise billing items for this invoice.
                    </p>
                  </div>
                </div>
              <div className="item-table invoice-items-card">
                <h2 className="jost-bold">Item Table</h2>
                <div className="invoice-table-scroll">
                <table className="invoice-items-table">
                  <thead>
                    <tr>
                      <th className="serviceid jost-bold">Type Of Services</th>
                      <th className="serviceid jost-bold">
                        Financial Year 
                      </th>
                      <th>Quantity</th>
                      <th>Rate</th>
                      <th>Tax</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index} className="row-table">
                        <td className="table-border">
                          {/* <Select
                            value={
                              item.serviceId
                                ? {
                                    label:
                                      services.find(
                                        (service) =>
                                          service.service_id === item.serviceId
                                      )?.service_name || item.type,
                                    value: item.serviceId,
                                  }
                                : null
                            }
                            onChange={(selectedOption) => {
                              if (selectedOption.value === "add-new") {
                                handleAddNewService(); // Call function to handle adding a new service
                              } else {
                                handleServiceSelection(index, selectedOption);
                              }
                            }}
                            options={[
                              ...services.map((service) => ({
                                value: service.service_id,
                                label: service.service_name,
                              })),
                              { value: "add-new", label: "➕ Add New Service" }, // Different label for distinction
                            ]}
                            getOptionLabel={(e) =>
                              e.value === "add-new" ? (
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    color: "#007bff",
                                  }}
                                >
                                  {e.label}
                                </span>
                              ) : (
                                e.label
                              )
                            }
                            placeholder="Select The Type Of Services"
                            classNamePrefix="react-select"
                            styles={{
                              menu: (base) => ({
                                ...base,
                                marginTop: 0,
                                borderRadius: 6,
                                zIndex: 1000, // Ensure dropdown is above other elements
                              }),
                              menuList: (base) => ({
                                ...base,
                                maxHeight: "200px", // Set a max height for better scrolling
                                overflowY: "auto",
                              }),
                              control: (base) => ({
                                ...base,
                                minHeight: "40px", // Increase height for better appearance
                                borderColor: "#ccc",
                              }),
                              option: (base, { data }) => ({
                                ...base,
                                color:
                                  data.value === "add-new"
                                    ? "#007bff"
                                    : "inherit",
                                fontWeight:
                                  data.value === "add-new" ? "bold" : "normal",
                                backgroundColor:
                                  data.value === "add-new"
                                    ? "#f8f9fa"
                                    : base.backgroundColor,
                              }),
                            }}
                          /> */}
                          <Select
                            value={
                              item.serviceId
                                ? {
                                  label:
                                    services.find(
                                      (service) =>
                                        service.service_id === item.serviceId
                                    )?.service_name || "Select Service",
                                  value: item.serviceId,
                                }
                                : null
                            }
                            onChange={(selectedOption) => {
                              if (selectedOption.value === "add-new") {
                                handleAddNewService(); // Call function to handle adding a new service
                              } else {
                                handleServiceSelection(index, selectedOption);
                              }
                            }}
                            options={[
                              { value: "add-new", label: "➕ Add New Service" },
                              ...services.map((service) => ({
                                value: service.service_id,
                                label: service.service_name,
                              })),
                            ]}
                            placeholder="Select The Type Of Services"
                            classNamePrefix="react-select"
                            menuPortalTarget={menuPortalTarget}
                            menuPosition="fixed"
                            menuShouldScrollIntoView={false}
                            styles={serviceDropdownStyles}
                          />
                        </td>

                        {isVisitorPopupOpen && (
                          <ServiceMasterModal
                            onClose={() => setIsVisitorPopupOpen(false)}
                          // handleAddNewService={handleAddNewService}
                          />
                        )}
                        <td className="table-border">
                          {/* <Select
                            value={
                              item.serviceId
                                ? {
                                    label:
                                      services.find(
                                        (service) =>
                                          service.service_id === item.serviceId
                                      )?.service_name || item.type,
                                    value: item.serviceId,
                                  }
                                : null
                            }
                            onChange={(selectedOption) => {
                              if (selectedOption.value === "add-new") {
                                handleAddNewService(); // Call function to handle adding a new service
                              } else {
                                handleServiceSelection(index, selectedOption);
                              }
                            }}
                            options={[
                              ...services.map((service) => ({
                                value: service.service_id,
                                label: service.service_name,
                              })),
                              { value: "add-new", label: "➕ Add New Service" }, // Different label for distinction
                            ]}
                            getOptionLabel={(e) =>
                              e.value === "add-new" ? (
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    color: "#007bff",
                                  }}
                                >
                                  {e.label}
                                </span>
                              ) : (
                                e.label
                              )
                            }
                            placeholder="Select The Type Of Services"
                            classNamePrefix="react-select"
                            styles={{
                              menu: (base) => ({
                                ...base,
                                marginTop: 0,
                                borderRadius: 6,
                                zIndex: 1000, // Ensure dropdown is above other elements
                              }),
                              menuList: (base) => ({
                                ...base,
                                maxHeight: "200px", // Set a max height for better scrolling
                                overflowY: "auto",
                              }),
                              control: (base) => ({
                                ...base,
                                minHeight: "40px", // Increase height for better appearance
                                borderColor: "#ccc",
                              }),
                              option: (base, { data }) => ({
                                ...base,
                                color:
                                  data.value === "add-new"
                                    ? "#007bff"
                                    : "inherit",
                                fontWeight:
                                  data.value === "add-new" ? "bold" : "normal",
                                backgroundColor:
                                  data.value === "add-new"
                                    ? "#f8f9fa"
                                    : base.backgroundColor,
                              }),
                            }}
                          /> */}
                          <Select
                            value={
                              item.financialYear
                                ? {
                                  label:
                                    financialYears.find(
                                      (fy) =>
                                        fy.id === item.financialYear
                                    )?.fy_label || "Select Financial Year",
                                  value: item.financialYear,
                                }
                                : null
                            }
                            onChange={(selectedOption) => {handleFYSelection(index, selectedOption);}}
                            options={
                             financialYears.map((fy) => ({
                                value: fy.id,
                                label: fy.fy_label,
                              }))
                            }
                            placeholder="Select The Financial Year"
                            classNamePrefix="react-select"
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                              }),
                              menu: (base) => ({
                                ...base,
                                zIndex: 9999,
                              }),
                            }}
                            menuPortalTarget={menuPortalTarget}
                            menuPosition="fixed"
                            menuShouldScrollIntoView={false}
                          />
                        </td>
                        <td className="table-border">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => {
                              const value = e.target.value;

                              // Allow empty input
                              if (value === "") {
                                handleItemChange(index, "quantity", "");
                                return;
                              }
                              // Only allow valid numbers
                              const number = parseFloat(value);
                              handleItemChange(
                                index,
                                "quantity",
                                isNaN(number) ? "" : number
                              );
                            }}
                            step="0.01"
                          />
                        </td>

                        <td className="table-border">
                          <input
                            type="number"
                            value={item.rate}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              handleItemChange(
                                index,
                                "rate",
                                newValue === "" ? "" : parseFloat(newValue) || 0
                              );
                            }}
                            step="0.01"
                          />
                        </td>

                        {/* <td className="table-border discount-cell">
                          <input
                            type="number"
                            value={item.discount}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              handleItemChange(
                                index,
                                "discount",
                                newValue === "" ? "" : parseFloat(newValue) || 0
                              );
                            }}
                            step="0.01"
                            min="0"
                            className="discount-input"
                          />
                        </td> */}

                        <td className="table-border">
                          <input
                            type="number"
                            value={item.tax || 0} // Show tax as 0 if no GST
                            readOnly
                            step="0.01"
                          />
                        </td>

                        <td>
                          <input
                            type="number"
                            value={Number(item.amount).toFixed(2)} // Ensures max two decimal places
                            readOnly
                            step="0.01"
                          />
                        </td>

                        <td>
                          <div
                            className="cancel-btn-row"
                            onClick={() => handleRemoveItem(index)}
                          >
                            <MdCancel />
                          </div>
                        </td>
                      </tr>
                    ))}

                    <tr className="row-table">
                      <td colSpan={7}>
                        <button
                          type="button"
                          className="add-row-btn"
                          onClick={addNewRow}
                        >
                          <span>+</span> Add New Row
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                </div>
                <div className="invoice-total-main">
                  <div className="invoice-totals tableBottom">
                    {/* Subtotal */}
                    <div className="total-row tableBottom-row">
                      <div className="total-label tableBottom-label">
                        Sub Total <br />
                      </div>
                      <div className="total-amount tableBottom-amount">
                        <input
                          type="number"
                          value={(Number(totalAmount) || 0).toFixed(2)} // Ensure totalAmount is a number
                          readOnly
                          step="0.01"
                        />
                      </div>
                    </div>

                    <div className="total-row tableBottom-row">
                      <div className="total-label tableBottom-label">
                        Total Discount
                        <br />
                        <Select
                          options={discountOptions}
                          value={discountOptions.find(
                            (option) =>
                              option.value ===
                              (discountType === "%"
                                ? "percentage"
                                : discountType)
                          )}
                          onChange={(selectedOption) => {
                            setdiscountType(selectedOption.value);
                          }}
                          styles={customStyles}
                          isSearchable={false}
                          placeholder="" // Correctly remove the placeholder
                        />
                      </div>
                      <div
                        className="total-amount tableBottom-amount"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          type="number"
                          value={totalDiscount}
                          onChange={(e) => {
                            let value = e.target.value;

                            // Allow empty string or valid numbers (including decimals)
                            if (value === "" || /^\d*\.?\d*$/.test(value)) {
                              // Keep empty string as is, only convert to number if not empty
                              const newValue = value === "" ? "" : Number(value);
                              setTotalDiscount(newValue);
                              setTimeout(() => {
                                updateTotals();
                              }, 0);
                            }
                          }}
                          step="0.01"
                          min="0"
                          max={discountType === "percentage" ? 100 : totalAmount}
                          style={{
                            // width: "50px",
                            padding: "5px",
                            textAlign: "center",
                          }}
                          onFocus={handleInputClickzeo}
                        />
                      </div>
                    </div>

                    <div className="total-row tableBottom-row">
                      <div className="total-label tableBottom-label">
                        Total amount
                      </div>
                      <div className="total-amount tableBottom-amount">
                        <input
                          type="number"
                          value={(
                            Number(totalAmountAfterDiscount) || 0
                          ).toFixed(2)} // Ensure totalAmountAfterDiscount is a number
                          readOnly
                          step="0.01"
                          onFocus={handleInputClickzeo}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </section>
            </div>
          </form>
        </div>
      </div>

      {/* Client Management Modal */}
      <Modal
        open={isCustomModalOpen}
        onClose={handleCloseCustomModal}
        disableEnforceFocus={isCreateFamilyModalOpen}
        disableAutoFocus={isCreateFamilyModalOpen}
        disableRestoreFocus={isCreateFamilyModalOpen}
        aria-labelledby="client-modal-title"
        aria-describedby="client-modal-description"
        sx={{
          zIndex: 1300,
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(15, 23, 42, 0.45)",
            backdropFilter: "blur(2px)",
            zIndex: 1299,
          },
        }}
      >
        <Box sx={modalStyle}>
          <div className="invoice-client-modal-shell">
            <div className="invoice-client-modal-header">
              <div>
                <h2 id="client-modal-title" className="invoice-client-modal-title">
                  New Client Management
                </h2>
                <p className="invoice-client-modal-subtitle">
                  Add a new client with billing, address and contact information.
                </p>
              </div>
              <button
                type="button"
                className="invoice-client-modal-close"
                onClick={handleCloseCustomModal}
                aria-label="Close client management modal"
              >
                <X size={18} />
              </button>
            </div>

            <div className="invoice-client-modal-body">
              <form
                id="invoice-client-create-form"
                onSubmit={handleModalSubmit}
                className="client-modal-form"
              >
              <div className="invoice-client-section-title-row">
                <h3 className="invoice-client-section-title">Basic Information</h3>
              </div>
              {/* Client Name */}
              <div
                className="client-form-group"
                style={{ marginBottom: "20px" }}
              >
                <label className="client-form-label">
                  Client Name <span className="client-required">*</span>
                </label>
                <input
                  ref={firstfield}
                  type="text"
                  className="client-input-field"
                  placeholder="Enter Client Name"
                  value={modalClientName}
                  maxLength={100}
                  onChange={(e) => {
                    setModalClientName(e.target.value);
                    if (modalErrors.clientName) {
                      setModalErrors((prev) => ({ ...prev, clientName: "" }));
                    }
                  }}
                  required
                />
                {modalErrors.clientName && (
                  <p className="client-error-message">
                    {modalErrors.clientName}
                  </p>
                )}
              </div>

              {/* Company Name */}
              <div
                className="client-form-group"
                style={{ marginBottom: "20px" }}
              >
                <label className="client-form-label">
                  Company/Firm Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="client-input-field"
                  placeholder="Enter Company/Firm Name"
                  value={modalCompanyName}
                  maxLength={150}
                  onChange={(e) => {
                    setModalCompanyName(e.target.value);
                    if (modalErrors.companyName) {
                      setModalErrors((prev) => ({ ...prev, companyName: "" }));
                    }
                  }}
                  required
                />
                {modalErrors.companyName && (
                  <p className="client-error-message">
                    {modalErrors.companyName}
                  </p>
                )}
              </div>

              {/* File Number */}
              <div
                className="client-form-group"
                style={{ marginBottom: "20px" }}
              >
                <label className="client-form-label">
                  File Number <span className="client-required">*</span>
                </label>
                <input
                  type="text"
                  className="client-input-field"
                  placeholder="Enter File Number"
                  value={modalFileNumber}
                  onChange={(e) => {
                    setModalFileNumber(e.target.value);
                    if (modalErrors.fileNumber) {
                      setModalErrors((prev) => ({ ...prev, fileNumber: "" }));
                    }
                  }}
                  required
                />
                {modalErrors.fileNumber && (
                  <p className="client-error-message">
                    {modalErrors.fileNumber}
                  </p>
                )}
              </div>

              {/* Company Type */}
              <div
                className="client-form-group"
                style={{ marginBottom: "20px" }}
              >
                <label className="client-form-label">
                  Company Type <span className="client-required">*</span>
                </label>
                <Select
                  className="select-field"
                  classNamePrefix="invoice-client-select"
                  value={
                    modalSelectedCompanyType
                      ? companyTypes.find(
                        (option) => option.value === modalSelectedCompanyType
                      )
                      : null
                  }
                  onChange={(selectedOption) => {
                    const value = selectedOption ? selectedOption.value : "";
                    setModalSelectedCompanyType(value);
                    if (modalErrors.companyType) {
                      setModalErrors((prev) => ({ ...prev, companyType: "" }));
                    }
                  }}
                  options={companyTypes}
                  placeholder="Select Company Type"
                  isClearable
                  styles={getModalClientSelectStyles(
                    Boolean(modalErrors.companyType),
                    1500
                  )}
                  menuPortalTarget={document.body}
                />
                {modalErrors.companyType && (
                  <p className="client-error-message">
                    {modalErrors.companyType}
                  </p>
                )}
              </div>

              <div className="invoice-client-section-title-row">
                <h3 className="invoice-client-section-title">
                  Billing &amp; Classification
                </h3>
              </div>
              {/* Opening Balance */}
              <div
                className="client-form-group invoice-client-span-2 invoice-client-contact-section"
                style={{ marginBottom: "20px" }}
              >
                <label className="client-form-label">
                  Opening Balance <span className="client-required">*</span>
                </label>
                <Select
                  className="select-field"
                  classNamePrefix="invoice-client-select"
                  value={modalSelectedOptionBalance}
                  options={balanceOptions}
                  placeholder="Select advance/pending"
                  isClearable
                  onChange={handleModalBalanceChange}
                  styles={getModalClientSelectStyles(false, 1500)}
                  menuPortalTarget={document.body}
                />
                  <div className="form-group" style={{ flexDirection: "column", marginBottom: "0px"}}>
                <label className="form-label">
                  Client Type <span className="required">*</span>
                </label>
                <div className="form-inputs">
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="client_type"
                        checked={clientType === "individual"}
                        onChange={() => handleClientTypeChange("individual")}
                      />
                      <span className="radio-text">Individual</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="client_type"
                        checked={clientType === "family group"}
                        onChange={() => handleClientTypeChange("family group")}
                      />
                      <span className="radio-text">Family Group</span>
                    </label>
                  </div>
                  {errors.clientType && (
                    <p className="error-message">{errors.clientType}</p>
                  )}
                </div>
              </div>
               {clientType === "family group" && (
                <div className="form-group">
                  <label className="form-label">Select Family Group</label>
                  <div className="form-inputs form-family-row">
                    <Select
                      className="select-field"
                      classNamePrefix="invoice-client-select"
                      value={
                        selectedFamilyId
                          ? familyGroups
                              .map((family) => ({
                                value: family.family_id,
                                label: family.family_name,
                              }))
                              .find((option) => option.value === selectedFamilyId) ||
                            null
                          : null
                      }
                      onChange={(selectedOption) => {
                        const nextFamilyId = selectedOption?.value || "";
                        setSelectedFamilyId(nextFamilyId);

                        if (!nextFamilyId) {
                          setContactPersons((prevContacts) =>
                            clearMappedFamilyContact(prevContacts)
                          );
                          return;
                        }

                        const selectedFamily = familyGroups.find(
                          (family) => family.family_id === nextFamilyId
                        );
                        mapFamilyContactToPrimary(selectedFamily);
                      }}
                      options={familyGroups.map((family) => ({
                        value: family.family_id,
                        label: family.family_name,
                      }))}
                      placeholder={
                        familyGroupsLoading
                          ? "Loading family groups..."
                          : "Select Family Group"
                      }
                      isClearable
                      isLoading={familyGroupsLoading}
                      menuPortalTarget={menuPortalTarget}
                      menuPosition="fixed"
                      styles={getModalClientSelectStyles(false, 9999)}
                    />
                    <button
                      type="button"
                      className="family-plus-btn"
                      aria-label="Add family group"
                      onClick={openCreateFamilyModal}
                    >
                      +
                    </button>
                    {familyGroupsError && (
                      <p className="error-message">{familyGroupsError}</p>
                    )}
                  </div>
                </div>
              )}
                {modalSelectedOptionBalance &&
                  modalSelectedOptionBalance.value !== "Financially Clear" && (
                    <input
                      type="number"
                      className="client-input-field"
                      style={{ marginTop: "10px" }}
                      placeholder="Enter Amount"
                      value={modalOpeningAmount}
                      maxLength={15}
                      onChange={(e) => {
                        setModalOpeningAmount(e.target.value);
                        if (modalErrors.openingAmount) {
                          setModalErrors((prev) => ({
                            ...prev,
                            openingAmount: "",
                          }));
                        }
                      }}
                    />
                  )}
                {(modalSelectedOptionBalance?.value !== "Financially Clear" && modalSelectedOptionBalance?.value) &&
                  modalErrors.openingAmount && (
                    <p className="client-error-message">
                      {modalErrors.openingAmount}
                    </p>
                  )}
              </div>

              <div className="invoice-client-section-title-row">
                <h3 className="invoice-client-section-title">Client Information</h3>
              </div>
              {/* Client Info: DOB and PAN */}
              <div
                className="client-form-group invoice-client-span-2"
                style={{ marginBottom: "20px" }}
              >
                <label className="client-form-label">
                  Client Info <span className="required">*</span>
                </label>
                <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: "50%" }}>
               <div>
               <DatePicker
  className="client-input-field"
  selected={
    modalDob
      ? (() => {
          const [day, month, year] = modalDob.split("-");
          return new Date(year, month - 1, day);
        })()
      : null
  }
  onChange={(date) => {
    if (!date) {
      setModalDob("");
      return;
    }
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const formatted = `${day}-${month}-${year}`;
    setModalDob(formatted);

    const { error } = normalizeBirthDateInput(formatted, { allowPartial: false });
    setModalDobError(error);

    if (modalErrors.dob) {
      setModalErrors((prev) => ({ ...prev, dob: "" }));
    }
  }}
  dateFormat="dd-MM-yyyy"
  placeholderText="DD-MM-YYYY"
  showMonthDropdown
  showYearDropdown          // 👈 dropdown instead of scroll
  dropdownMode="select"     // 👈 this is the key — renders a <select> for year
  maxDate={new Date()}      // prevents future dates
  yearDropdownItemNumber={100}
  scrollableYearDropdown
  required
/></div>
                    {modalErrors.dob && (
                      <p
                        className="client-error-message"
                        style={{
                          marginTop: "5px",
                          color: "red",
                          fontSize: "12px",
                        }}
                      >
                        {modalErrors.dob}
                      </p>
                    )}
                    <small className="date-format-hint">Enter Birth Date/Date Of Incorporation</small>
                  </div>

                  <div style={{ flex: 1, minWidth: "200px" }}>
                    <input
                      type="text"
                      className="client-input-field"
                      placeholder="Enter PAN Number"
                      value={modalPan}
                      maxLength={10}
                      onChange={handleModalPanChange}
                      required
                    />
                    {modalErrors.pan && (
                      <p
                        className="client-error-message"
                        style={{
                          marginTop: "5px",
                          color: "red",
                          fontSize: "12px",
                        }}
                      >
                        {modalErrors.pan}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="invoice-client-section-title-row">
                <h3 className="invoice-client-section-title">Address Details</h3>
              </div>
              {/* Address Fields */}
              <div
                className="client-form-group invoice-client-span-2"
                style={{ marginBottom: "20px" }}
              >
                <label className="client-form-label">
                  Client Address <span className="client-required">*</span>
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "15px",
                    marginBottom: "15px",
                  }}
                >
                  <div>
                    <input
                      type="text"
                      className="client-input-field"
                      placeholder="Enter office number"
                      value={modalAddress.street}
                      onChange={(e) =>
                        handleModalAddressChange("street", e.target.value)
                      }
                      required
                    />
                    {modalErrors.street && (
                      <p className="client-error-message">{modalErrors.street}</p>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      className="client-input-field"
                      placeholder="Name of premises"
                      value={modalAddress.city}
                      maxLength={100}
                      onChange={(e) => {
                        handleModalAddressChange("city", e.target.value);
                        if (modalErrors.city) {
                          setModalErrors((prev) => ({ ...prev, city: "" }));
                        }
                      }}
                      required
                    />
                    {modalErrors.city && (
                      <p className="client-error-message">{modalErrors.city}</p>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "15px",
                  }}
                >
                  <div>
                    <input
                      type="text"
                      className="client-input-field"
                      placeholder="Area"
                      value={modalAddress.state}
                      maxLength={100}
                      onChange={(e) => {
                        handleModalAddressChange("state", e.target.value);
                        if (modalErrors.area) {
                          setModalErrors((prev) => ({ ...prev, area: "" }));
                        }
                      }}
                      required
                    />
                    {modalErrors.area && (
                      <p className="client-error-message">{modalErrors.area}</p>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      className="client-input-field"
                      placeholder="Postal code"
                      value={modalAddress.postalCode}
                      maxLength={6}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 6);
                        handleModalAddressChange("postalCode", value);
                        if (modalErrors.postalCode) {
                          setModalErrors((prev) => ({ ...prev, postalCode: "" }));
                        }
                      }}
                      required
                    />
                    {modalErrors.postalCode && (
                      <p className="client-error-message">
                        {modalErrors.postalCode}
                      </p>
                    )}
                  </div>
                </div>
                {modalErrors.address && (
                  <p className="client-error-message">{modalErrors.address}</p>
                )}
              </div>

              <div className="invoice-client-section-title-row invoice-client-section-title-row-actions">
                <h3 className="invoice-client-section-title">
                  Contact Person Details
                </h3>
                <button
                  type="button"
                  className="invoice-client-inline-action"
                  onClick={handleModalAddContactPerson}
                >
                  <LuPlus size={16} />
                  <span>Add More Contact Person</span>
                </button>
              </div>
              {/* Contact Person Details */}
              <div
                className="client-form-group invoice-client-span-2"
                style={{ marginBottom: "20px" }}
              >
                <label className="client-form-label">
                  Contact Person Details{" "}
                  <span className="client-required">*</span>
                </label>
                {contactPersons.map((contact, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: "15px",
                      padding: "15px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: "15px",
                        marginBottom: "15px",
                      }}
                    >
                      <div>
                        <input
                          type="text"
                          className="client-input-field"
                          placeholder="Enter Contact Person Name"
                          value={contact.name}
                          maxLength={100}
                          onChange={(e) =>
                            handleModalContactChange(
                              index,
                              "name",
                              e.target.value
                            )
                          }
                          required
                        />
                        {modalErrors[`name_${index}`] && (
                          <p className="client-error-message">
                            {modalErrors[`name_${index}`]}
                          </p>
                        )}
                      </div>
                      <div>
                        <input
                          type="email"
                          className="client-input-field"
                          placeholder="Enter Contact Person Email"
                          value={contact.email}
                          maxLength={100}
                          onChange={(e) =>
                            handleModalContactChange(
                              index,
                              "email",
                              e.target.value
                            )
                          }
                          required
                        />
                        {modalErrors[`email_${index}`] && (
                          <p className="client-error-message">
                            {modalErrors[`email_${index}`]}
                          </p>
                        )}
                      </div>
                      <div>
                        <input
                          type="text"
                          className="client-input-field"
                          placeholder="Enter Phone Number"
                          value={contact.phone}
                          maxLength={10}
                          inputMode="numeric"
                          onChange={(e) => {
                            const value = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 10);
                            handleModalContactChange(index, "phone", value);
                          }}
                          required
                        />
                        {modalErrors[`phone_${index}`] && (
                          <p className="client-error-message">
                            {modalErrors[`phone_${index}`]}
                          </p>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <label style={{ marginRight: "10px" }}>
                          Set as Primary Contact?
                        </label>
                        <input
                          type="radio"
                          name="primaryContact"
                          checked={contact.isPrimary ?? index === 0}
                          onChange={() => handleModalPrimaryChange(index)}
                        />
                      </div>
                      {contactPersons.length > 1 && (
                        <button
                          type="button"
                          className="invoice-client-remove-btn"
                          onClick={() => handleModalRemoveContactPerson(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    {modalErrors[`contact_${index}`] && (
                      <p className="error-message">
                        {modalErrors[`contact_${index}`]}
                      </p>
                    )}
                  </div>
                ))}

              </div>

              <div className="invoice-client-section-title-row">
                <h3 className="invoice-client-section-title">
                  Billing Company &amp; Terms
                </h3>
              </div>
              {/* Customer Billing Company (Subsidiary) */}
              <div
                className="client-form-group"
                style={{ marginBottom: "20px" }}
              >
                <label className="form-label">
                  Customer Billing Company (Subsidiary){" "}
                  <span className="required">*</span>
                </label>
                <Select
                  className="select-field"
                  classNamePrefix="invoice-client-select"
                  value={
                    modalSelectedSubsidary
                      ? modalSubsidaries.find(
                        (sub) => sub.value === modalSelectedSubsidary
                      )
                      : null
                  }
                  onChange={(selectedOption) => {
                    const selectedId = selectedOption
                      ? selectedOption.value
                      : null;
                    setModalSelectedSubsidary(selectedId);
                    if (modalErrors.subsidiary) {
                      setModalErrors((prev) => ({ ...prev, subsidiary: "" }));
                    }
                  }}
                  options={modalSubsidaries.map((sub) => ({
                    value: sub.value,
                    label: `${sub.label} (${sub.tag_name})`,
                  }))}
                  placeholder="Select Type Of Client"
                  isClearable
                  isLoading={modalLoading}
                  styles={getModalClientSelectStyles(
                    Boolean(modalErrors.subsidiary),
                    1500
                  )}
                />
                {modalErrors.subsidiary && (
                  <p className="client-error-message">
                    {modalErrors.subsidiary}
                  </p>
                )}
              </div>

              {/* Payment Terms */}
              <div
                className="client-form-group"
                style={{ marginBottom: "20px" }}
              >
                <label className="client-form-label">
                  Payment Terms <span className="client-required">*</span>
                </label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Enter Payment Terms"
                    value={modalPaymentTerm}
                    onChange={(e) => setModalPaymentTerm(e.target.value)}
                    required
                    style={{ flex: 1, minWidth: "130px" }}
                  />
                  <span>Days</span>
                </div>
                {modalErrors.paymentTerm && (
                  <p className="client-error-message">
                    {modalErrors.paymentTerm}
                  </p>
                )}
              </div>

            </form>
          </div>
          <div className="invoice-client-modal-footer">
            <button
              type="button"
              className="invoice-client-secondary-btn"
              onClick={handleCloseCustomModal}
              disabled={modalLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="invoice-client-create-form"
              className="invoice-client-primary-btn"
              onClick={handleModalSubmit}
              disabled={modalLoading || modalIsSaved || isModalSubmitting}
            >
              {isModalSubmitting ? (
                <span className="invoice-client-button-content">
                  <ClipLoader size={16} color="#FFF" />
                  Creating Client...
                </span>
              ) : modalLoading ? (
                "Saving..."
              ) : modalIsSaved ? (
                "Saved"
              ) : (
                "Save Client"
              )}
            </button>
          </div>
          </div>
          <Snackbar
            open={snackbarOpen && isCustomModalOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            style={{
              transform: "translateY(50px)",
              marginLeft: "20px",
              zIndex: isCustomModalOpen ? 1400 : undefined,
            }}
          >
            <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
              {snackbarMessage}
            </MuiAlert>
          </Snackbar>
        </Box>

      </Modal>

      {isPopupOpen && (
        <ClientModal onClose={closeModal} fetchValue={handleFetchValue} />
      )}
      {isCreateFamilyModalOpen &&
        familyModalPortalTarget &&
        createPortal(
          <div className="invoice-family-modal-root">
            <div className="invoice-family-modal-overlay" />
            <div className="invoice-family-modal-wrapper">
              <div className="invoice-family-modal-content">
                <div className="invoice-family-modal-header">
                  <div>
                    <h2 className="invoice-family-modal-title">Create New Family Group</h2>
                    <p className="invoice-family-modal-subtitle">
                      Add a family group to associate with this client.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="invoice-family-modal-close"
                    onClick={closeCreateFamilyModal}
                    aria-label="Close family group modal"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="invoice-family-modal-body">
                  <div className="invoice-family-form-stack">
                    <div className="client-form-group">
                      <label className="client-form-label">
                        Family Name <span className="client-required">*</span>
                      </label>
                      <input
                        ref={createFamilyFirstInputRef}
                        type="text"
                        className={`invoice-family-input-field ${
                          createFamilyErrors.family_name ? "client-input-error" : ""
                        }`}
                        autoFocus
                        placeholder="Enter Family Name"
                        value={createFamilyForm.family_name}
                        onChange={(e) => {
                          setCreateFamilyForm((prev) => ({
                            ...prev,
                            family_name: e.target.value,
                          }));
                          setCreateFamilyErrors((prev) => ({
                            ...prev,
                            family_name: "",
                          }));
                        }}
                      />
                      {createFamilyErrors.family_name && (
                        <p className="client-error-message">
                          {createFamilyErrors.family_name}
                        </p>
                      )}
                    </div>
                    <div className="client-form-group">
                      <label className="client-form-label">
                        Contact Name <span className="client-required">*</span>
                      </label>
                      <input
                        type="text"
                        className={`invoice-family-input-field ${
                          createFamilyErrors.contact_name ? "client-input-error" : ""
                        }`}
                        placeholder="Enter Contact Name"
                        value={createFamilyForm.contact_name}
                        onChange={(e) => {
                          setCreateFamilyForm((prev) => ({
                            ...prev,
                            contact_name: e.target.value,
                          }));
                          setCreateFamilyErrors((prev) => ({
                            ...prev,
                            contact_name: "",
                          }));
                        }}
                      />
                      {createFamilyErrors.contact_name && (
                        <p className="client-error-message">
                          {createFamilyErrors.contact_name}
                        </p>
                      )}
                    </div>
                    <div className="client-form-group">
                      <label className="client-form-label">
                        Phone Number <span className="client-required">*</span>
                      </label>
                      <input
                        type="text"
                        className={`invoice-family-input-field ${
                          createFamilyErrors.phone_number ? "client-input-error" : ""
                        }`}
                        placeholder="Enter Phone Number"
                        value={createFamilyForm.phone_number}
                        maxLength={10}
                        onChange={(e) => {
                          const phoneValue = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 10);
                          setCreateFamilyForm((prev) => ({
                            ...prev,
                            phone_number: phoneValue,
                          }));
                          setCreateFamilyErrors((prev) => ({
                            ...prev,
                            phone_number: "",
                          }));
                        }}
                      />
                      {createFamilyErrors.phone_number && (
                        <p className="client-error-message">
                          {createFamilyErrors.phone_number}
                        </p>
                      )}
                    </div>
                    <div className="client-form-group">
                      <label className="client-form-label">
                        Email Id <span className="client-required">*</span>
                      </label>
                      <input
                        type="email"
                        className={`invoice-family-input-field ${
                          createFamilyErrors.email ? "client-input-error" : ""
                        }`}
                        placeholder="Enter Email Id"
                        value={createFamilyForm.email}
                        onChange={(e) => {
                          setCreateFamilyForm((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }));
                          setCreateFamilyErrors((prev) => ({ ...prev, email: "" }));
                        }}
                      />
                      {createFamilyErrors.email && (
                        <p className="client-error-message">{createFamilyErrors.email}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="invoice-family-modal-buttons">
                  <button
                    type="button"
                    className="invoice-client-secondary-btn"
                    onClick={closeCreateFamilyModal}
                    disabled={isCreatingFamily}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="invoice-client-primary-btn"
                    onClick={handleCreateFamily}
                    disabled={isCreatingFamily}
                  >
                    {isCreatingFamily ? "Creating..." : "Create"}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          familyModalPortalTarget
        )}
      {isModalOpen && (
        <ServiceMasterModal
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveService}
        />
      )}

      <Snackbar
        open={snackbarOpen && !isCustomModalOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ transform: "translateY(50px)", marginLeft: "20px" ,zIndex: isCustomModalOpen ? 1400 : undefined }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
      <div className="form-footer">
        <div className="button-group">
          <button
            type="button"
            className="btn btn-cancel"
            onClick={handleBack}
            disabled={isSubmitting}
          >
            CANCEL
          </button>
          <button
            type="submit"
            className="btn btn-save"
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              opacity: isSubmitting ? 0.7 : 1,
              cursor: isSubmitting ? "not-allowed" : "pointer",
            }}
          >
            {isSubmitting ? (
              <span className="button-content">
                <ClipLoader size={16} color="#FFF" />
                <span style={{ marginLeft: "8px" }}>
                  {snackbarSeverity === "success"
                    ? "Invoice Created!"
                    : "Creating Invoice..."}
                </span>
              </span>
            ) : (
              "SAVE"
            )}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}
